/**
 * 확인서 발송 파이프라인 — apps.leferi.com/confirmations
 *
 * 영상(화면 기록 2026-09-30)의 흐름을 포털에 이식 — 판매상품 추가(미리보기 전용).
 *   기회 선택 → 그 기회의 판매상품(OpportunityLineItem) 목록 → "판매상품 추가" →
 *     제품 다중 선택(Standard Price Book) → 제품별 폼(귀속부서·카테고리 picklist,
 *     크리에이터·판매단가·정산일 등) → 추가(미리보기).
 *   ⚠️ 저장(Salesforce 반영)은 다음 단계 — 지금은 쓰기 없음.
 *
 * 필드 매핑은 레코멘드 템플릿(oppProductTemplates.ts)과 동일한 OpportunityLineItem 필드.
 * picklist(귀속부서/사업·제품 카테고리, 제품=종속)는 /product-fields 에서 로드.
 *
 * chrome 은 SalesforceProducts/PdfCompressor 미러링 (LCMD): 사각 모서리, 핑크 CTA,
 * brown ink, ▪ 마커, no emoji.
 */
import { useEffect, useRef, useState } from 'react'
import {
  AlertCircle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Eye,
  FileCheck2,
  FileText,
  Mail,
  Plus,
  Search,
  Send,
  Trash2,
  Upload,
  X as XIcon,
} from 'lucide-react'
import { Link, useSearchParams } from 'react-router-dom'
import type { AuthSession } from './auth'
import {
  ConfirmationError,
  createDrafts,
  createLineItems,
  getConfirmationOpportunities,
  getMailStatus,
  getOpportunityLineItems,
  getOpportunityOwners,
  getOpportunityQuotes,
  getProductFields,
  generatePdfUrl,
  getPdfTemplates,
  previewPdfUrl,
  humanizeConfirmationError,
  LECOMMEND_FROM,
  assignCreators,
  createConfirmations,
  previewAssignCreators,
  searchCreators,
  sendDrafts,
  searchProducts,
  quotePdfUrl,
  saveGeneratedPdf,
  uploadQuotePdf,
  type AssignResult,
  type CreateLineItemInput,
  type MailStatus,
  type CreatorRow,
  type LineItemRow,
  type OpportunityRow,
  type ProductFields,
  type ProductRow,
  type QuoteLine,
  type QuoteRow,
} from './confirmationsApi'

/** 미리보기로 추가한 판매상품(아직 SF 미반영). */
interface DraftProduct {
  key: string
  product: ProductRow
  department: string
  brandItem: string
  optionName: string
  businessCategory: string
  itemCategory: string
  creator: CreatorRow | null
  unitPrice: string
  contentsFee: string
  pricePrj: string
  prepayDate: string
  deferredDate: string
  uploadDate: string
}

export function ConfirmationDocsPage({ session: _session }: { session: AuthSession }) {
  const [picked, setPicked] = useState<OpportunityRow | null>(null)
  // 기회 선택을 URL(?opp=)에 쌓아, 브라우저 뒤로가기가 메인이 아니라 기회 목록으로 돌아오게.
  const [params, setParams] = useSearchParams()
  const oppParam = params.get('opp')
  // 뷰는 URL(?opp)과 picked.id 일치로 결정 — 뒤로가기로 opp 가 사라지면 자동으로 목록.
  const showDetail = Boolean(picked && oppParam && picked.oppId === oppParam)

  const pick = (o: OpportunityRow) => {
    setPicked(o)
    setParams({ opp: o.oppId }) // history push → 뒤로가기 시 목록으로
  }
  const back = () => setParams({})

  return (
    <div className="pt-s-7">
      <Link
        to="/?tab=leferist"
        className="inline-flex items-center gap-s-2 rounded-r-1 border border-line-hairline bg-surface-base px-s-3 py-s-2 lf-body-sm text-fg-2 transition-colors duration-base ease-standard hover:border-line-soft hover:bg-surface-soft hover:text-fg-1 focus-visible:outline-none focus-visible:shadow-focus"
      >
        <ChevronLeft size={14} strokeWidth={1.5} />
        Apps
      </Link>

      <div className="mt-s-6">
        <p className="lf-eyebrow">▪ Salesforce · 확인서 발송</p>
        <h1 className="mt-s-2 lf-display-2">Add opportunity products.</h1>
        <p className="mt-s-3 max-w-[720px] lf-body text-fg-2">
          기회를 고르면 그 기회의 <span className="text-fg-1">판매상품</span>이 떠요. "판매상품 추가"로
          제품을 고르고 단가·크리에이터·정산일 등을 입력해 추가해요. <span className="text-fg-1">지금은
          미리보기까지</span> — 저장(Salesforce 반영)은 다음 단계에서 연결돼요.
        </p>
      </div>

      {/* picker 는 언마운트하지 않고 숨김 — 상세 갔다 와도 검색·소유자·목록 그대로 유지. */}
      <div hidden={showDetail}>
        <OpportunityPicker onPick={pick} />
      </div>
      {showDetail && picked ? <OpportunityDetail opp={picked} onBack={back} /> : null}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Step 1 — 기회 선택                                                           */
/* -------------------------------------------------------------------------- */

function OpportunityPicker({ onPick }: { onPick: (o: OpportunityRow) => void }) {
  const [query, setQuery] = useState('')
  const [search, setSearch] = useState('')
  const [rows, setRows] = useState<OpportunityRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [hasMore, setHasMore] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [ownerId, setOwnerId] = useState('')
  const [owners, setOwners] = useState<{ id: string; name: string }[]>([])
  const LIMIT = 50

  useEffect(() => {
    const t = setTimeout(() => setSearch(query.trim()), 300)
    return () => clearTimeout(t)
  }, [query])

  // 소유자 목록(한 번).
  useEffect(() => {
    let live = true
    getOpportunityOwners()
      .then((r) => live && setOwners(r.rows))
      .catch(() => live && setOwners([]))
    return () => {
      live = false
    }
  }, [])

  useEffect(() => {
    let live = true
    setLoading(true)
    setError(null)
    getConfirmationOpportunities(search, LIMIT, 0, ownerId || undefined)
      .then((r) => {
        if (!live) return
        setRows(r.rows)
        setHasMore(r.hasMore)
        setLoading(false)
      })
      .catch((e: unknown) => {
        if (!live) return
        setError(e instanceof ConfirmationError ? humanizeConfirmationError(e.message) : '기회 조회 실패')
        setRows([])
        setHasMore(false)
        setLoading(false)
      })
    return () => {
      live = false
    }
  }, [search, ownerId])

  const loadMore = () => {
    setLoadingMore(true)
    getConfirmationOpportunities(search, LIMIT, rows.length, ownerId || undefined)
      .then((r) => {
        setRows((prev) => [...prev, ...r.rows])
        setHasMore(r.hasMore)
      })
      .catch(() => setHasMore(false))
      .finally(() => setLoadingMore(false))
  }

  return (
    <div className="mt-s-6">
      <div className="flex flex-wrap items-center gap-s-3">
        <label className="relative block min-w-[240px] flex-1">
          <span className="pointer-events-none absolute left-s-3 top-1/2 -translate-y-1/2 text-fg-3">
            <Search size={16} strokeWidth={1.6} />
          </span>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="기회 검색 — 브랜드/상품명 (예: 에뛰드, 토리든)"
            className="w-full rounded-r-1 border border-line-soft bg-surface-base py-s-3 pl-s-9 pr-s-4 lf-body text-fg-1 placeholder:text-fg-3 focus-visible:border-brand-blue focus-visible:outline-none focus-visible:shadow-focus"
          />
        </label>
        <select
          value={ownerId}
          onChange={(e) => setOwnerId(e.target.value)}
          aria-label="기회 소유자 필터"
          className="w-[200px] rounded-r-1 border border-line-soft bg-surface-base px-s-3 py-s-3 lf-body-sm text-fg-1 focus-visible:border-brand-blue focus-visible:outline-none focus-visible:shadow-focus"
        >
          <option value="">소유자 — 전체</option>
          {owners.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </select>
      </div>
      <p className="mt-s-2 lf-caption text-fg-3">
        ▪ 최근 수정순으로 기회를 보여줘요. 검색으로 좁힐 수 있어요.
        {!loading ? <span className="lf-num"> · {rows.length.toLocaleString()}개 표시{hasMore ? '+' : ''}</span> : null}
      </p>

      {error ? <p className="mt-s-4 lf-body-sm text-status-danger">▪ {error}</p> : null}

      <div className="mt-s-5 overflow-hidden border border-line-hairline">
        {loading ? (
          <p className="px-s-4 py-s-6 text-center lf-body-sm text-fg-3">불러오는 중…</p>
        ) : rows.length === 0 ? (
          <p className="px-s-4 py-s-6 text-center lf-body-sm text-fg-3">
            {search ? '검색 결과가 없어요.' : '표시할 기회가 없어요.'}
          </p>
        ) : (
          <>
            <ul className="flex flex-col">
              {rows.map((o) => (
                <li key={o.oppId}>
                  <button
                    type="button"
                    onClick={() => onPick(o)}
                    className="flex w-full items-center gap-s-4 border-b border-line-hairline px-s-4 py-s-3 text-left transition-colors duration-base ease-standard hover:bg-surface-soft focus-visible:outline-none focus-visible:shadow-focus"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate lf-body text-fg-1">{o.oppName}</p>
                      <p className="mt-s-1 flex flex-wrap items-center gap-x-s-3 gap-y-px lf-caption text-fg-3">
                        {o.account ? <span>{o.account}</span> : null}
                        {o.stage ? (
                          <span className="inline-flex rounded-r-1 border border-line-soft px-s-2 py-px text-fg-2">
                            {o.stage}
                          </span>
                        ) : null}
                      </p>
                    </div>
                    <span className="flex flex-shrink-0 items-center gap-s-2">
                      <span className="lf-num lf-body-sm text-fg-2">판매상품 {o.productCount}</span>
                      <ChevronRight size={16} strokeWidth={1.6} className="text-fg-3" />
                    </span>
                  </button>
                </li>
              ))}
            </ul>
            {hasMore ? (
              <button
                type="button"
                onClick={loadMore}
                disabled={loadingMore}
                className="w-full border-t border-line-hairline px-s-4 py-s-3 lf-body-sm text-fg-2 transition-colors duration-base ease-standard hover:bg-surface-soft disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loadingMore ? '불러오는 중…' : '더 보기'}
              </button>
            ) : null}
          </>
        )}
      </div>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Step 2 — 기회 상세: 판매상품 목록 + 추가(미리보기)                           */
/* -------------------------------------------------------------------------- */

function OpportunityDetail({ opp, onBack }: { opp: OpportunityRow; onBack: () => void }) {
  const [items, setItems] = useState<LineItemRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [drafts, setDrafts] = useState<DraftProduct[]>([])
  const [adding, setAdding] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveMsg, setSaveMsg] = useState<{ tone: 'ok' | 'warn' | 'danger'; text: string } | null>(null)
  // 판매상품 선택(PDF 만들기용) + 필터.
  const [checked, setChecked] = useState<Set<string>>(new Set())
  const [fProduct, setFProduct] = useState('')
  const [fCreator, setFCreator] = useState('')
  const [fAmountMin, setFAmountMin] = useState('')
  const [fAmountMax, setFAmountMax] = useState('')
  const [sending, setSending] = useState(false) // 확인서 목록
  // PDF 만들기 + 발송 대상(체크한 판매상품). null 이면 판매상품 화면.
  const [wizardItems, setWizardItems] = useState<LineItemRow[] | null>(null)
  // 크리에이터 지정 대상(크리에이터 없는 판매상품).
  const [assignFor, setAssignFor] = useState<LineItemRow | null>(null)
  // 판매상품별 확인서 발송 상태 — 이 기회의 확인서를 불러와 판매상품에 연결.
  const [quotes, setQuotes] = useState<QuoteRow[] | null>(null)
  const [quotesKey, setQuotesKey] = useState(0)
  useEffect(() => {
    let live = true
    getOpportunityQuotes(opp.oppId, '전체')
      .then((r) => live && setQuotes(r.rows))
      .catch(() => live && setQuotes([]))
    return () => {
      live = false
    }
  }, [opp.oppId, quotesKey])
  const quoteOf = (() => {
    const byOli = new Map<string, QuoteRow>()
    const byCreator = new Map<string, QuoteRow[]>()
    for (const q of quotes ?? []) {
      for (const l of q.lines) if (l.oliId) byOli.set(l.oliId, q)
      if (q.creatorId) byCreator.set(q.creatorId, [...(byCreator.get(q.creatorId) ?? []), q])
    }
    return (it: LineItemRow): QuoteRow | null => {
      const same = it.creatorId ? (byCreator.get(it.creatorId) ?? []) : []
      return byOli.get(it.id) ?? (same.length === 1 ? same[0] : null)
    }
  })()

  const load = () => {
    setLoading(true)
    setError(null)
    setQuotesKey((k) => k + 1)
    return getOpportunityLineItems(opp.oppId)
      .then((r) => setItems(r.rows))
      .catch((e: unknown) =>
        setError(e instanceof ConfirmationError ? humanizeConfirmationError(e.message) : '판매상품 조회 실패'),
      )
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    let live = true
    setLoading(true)
    setError(null)
    getOpportunityLineItems(opp.oppId)
      .then((r) => live && setItems(r.rows))
      .catch((e: unknown) => {
        if (live)
          setError(
            e instanceof ConfirmationError ? humanizeConfirmationError(e.message) : '판매상품 조회 실패',
          )
      })
      .finally(() => live && setLoading(false))
    return () => {
      live = false
    }
  }, [opp.oppId])

  const addDrafts = (ds: DraftProduct[]) => {
    setSaveMsg(null)
    setDrafts((prev) => [...prev, ...ds])
  }
  const removeDraft = (key: string) => setDrafts((prev) => prev.filter((d) => d.key !== key))

  // ⚠️ 프로덕션 Salesforce 쓰기 — 확인 모달에서 "생성"을 눌렀을 때만.
  const save = async () => {
    setConfirming(false)
    setSaving(true)
    setSaveMsg(null)
    const payload: CreateLineItemInput[] = drafts.map((d) => ({
      pbeId: d.product.pbeId,
      department: d.department,
      brandItem: d.brandItem,
      optionName: d.optionName,
      businessCategory: d.businessCategory,
      itemCategory: d.itemCategory,
      creatorId: d.creator?.id ?? null,
      unitPrice: d.unitPrice,
      contentsFee: d.contentsFee,
      pricePrj: d.pricePrj,
      prepayDate: d.prepayDate,
      deferredDate: d.deferredDate,
      uploadDate: d.uploadDate,
    }))
    try {
      const r = await createLineItems(opp.oppId, payload)
      // 생성 성공한 draft 는 목록에서 제거, 실패분은 남겨 재시도.
      const failedKeys = new Set(
        r.results.filter((x) => x.status === 'failed').map((x) => drafts[x.index]?.key),
      )
      setDrafts((prev) => prev.filter((d) => failedKeys.has(d.key)))
      await load()
      if (r.summary.failed === 0) {
        setSaveMsg({ tone: 'ok', text: `${r.summary.created}건을 Salesforce에 생성했어요.` })
      } else {
        const firstErr = r.results.find((x) => x.status === 'failed')?.error
        setSaveMsg({
          tone: 'warn',
          text: `${r.summary.created}건 생성, ${r.summary.failed}건 실패. 실패분은 아래에 남겨뒀어요.${
            firstErr ? ` (${firstErr})` : ''
          }`,
        })
      }
    } catch (e) {
      setSaveMsg({
        tone: 'danger',
        text: e instanceof ConfirmationError ? humanizeConfirmationError(e.message) : '저장 실패',
      })
    } finally {
      setSaving(false)
    }
  }

  // 필터 + 선택(체크박스). 매출액은 최소~최대 범위.
  const minAmount = fAmountMin.trim() ? Number(fAmountMin.replace(/[,\s]/g, '')) : null
  const maxAmount = fAmountMax.trim() ? Number(fAmountMax.replace(/[,\s]/g, '')) : null
  const filtered = items.filter((it) => {
    const amt = it.totalPrice ?? 0
    if (fProduct && !`${it.product ?? ''} ${it.brandItem ?? ''} ${it.optionName ?? ''}`.toLowerCase().includes(fProduct.trim().toLowerCase())) return false
    if (fCreator && !(it.creator ?? '').toLowerCase().includes(fCreator.trim().toLowerCase())) return false
    if (minAmount != null && Number.isFinite(minAmount) && amt < minAmount) return false
    if (maxAmount != null && Number.isFinite(maxAmount) && amt > maxAmount) return false
    return true
  })
  const filteredIds = filtered.map((it) => it.id)
  const allChecked = filteredIds.length > 0 && filteredIds.every((id) => checked.has(id))
  const someChecked = checked.size > 0 && !allChecked
  const toggleAll = () => setChecked(allChecked ? new Set() : new Set(filteredIds))
  const toggleOne = (id: string) =>
    setChecked((prev) => {
      const n = new Set(prev)
      if (n.has(id)) n.delete(id)
      else n.add(id)
      return n
    })

  return (
    <div className="mt-s-6">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-s-2 rounded-r-1 border border-line-hairline bg-surface-base px-s-3 py-s-2 lf-body-sm text-fg-2 transition-colors duration-base ease-standard hover:border-line-soft hover:bg-surface-soft hover:text-fg-1 focus-visible:outline-none focus-visible:shadow-focus"
      >
        <ChevronLeft size={14} strokeWidth={1.5} />
        기회 목록
      </button>

      <div className="mt-s-4 min-w-0">
        <h2 className="lf-h2 text-fg-1">{opp.oppName}</h2>
        <p className="mt-s-1 flex flex-wrap items-center gap-x-s-3 gap-y-px lf-caption text-fg-3">
          {opp.account ? <span>{opp.account}</span> : null}
          {opp.stage ? (
            <span className="inline-flex rounded-r-1 border border-line-soft px-s-2 py-px text-fg-2">
              {opp.stage}
            </span>
          ) : null}
        </p>
      </div>

      {wizardItems ? (
        <SendWizard
          opp={opp}
          items={wizardItems}
          onBack={() => {
            setWizardItems(null)
            setQuotesKey((k) => k + 1) // 방금 만든 확인서·발송 상태 반영
          }}
          onOpenList={() => {
            setWizardItems(null)
            setSending(true)
          }}
        />
      ) : sending ? (
        <ConfirmationsTab
          opp={opp}
          onBack={() => {
            setSending(false)
            setQuotesKey((k) => k + 1)
          }}
        />
      ) : (
        <>
      {/* 툴바: 필터 + PDF 만들기 + 판매상품 추가 */}
      <div className="mt-s-5 flex flex-wrap items-end justify-between gap-s-4">
        <div className="flex flex-wrap items-end gap-s-3">
          <FilterInput label="판매상품" value={fProduct} onChange={setFProduct} placeholder="상품·브랜드명" />
          <FilterInput label="크리에이터" value={fCreator} onChange={setFCreator} placeholder="크리에이터명" />
          <FilterInput label="매출액 (최소)" value={fAmountMin} onChange={setFAmountMin} placeholder="예: 0" numeric narrow />
          <FilterInput label="매출액 (최대)" value={fAmountMax} onChange={setFAmountMax} placeholder="예: 10000000" numeric narrow />
        </div>
        <div className="flex flex-shrink-0 items-center gap-s-2">
          {/* 체크한 판매상품의 크리에이터 → 확인서 PDF 생성 + 메일 발송. */}
          <button
            type="button"
            disabled={checked.size === 0}
            title={checked.size === 0 ? '확인서를 보낼 판매상품을 체크해 주세요.' : undefined}
            onClick={() => setWizardItems(items.filter((it) => checked.has(it.id)))}
            className="inline-flex items-center gap-s-2 rounded-r-2 bg-brand-pink px-s-4 py-s-3 lf-h4 text-brand-brown transition-colors duration-base ease-standard hover:bg-pink-500 focus-visible:outline-none focus-visible:shadow-focus disabled:cursor-not-allowed disabled:opacity-50"
          >
            <FileCheck2 size={15} strokeWidth={1.6} />
            PDF 만들기{checked.size ? ` (${checked.size})` : ''}
          </button>
          {/* 확인서 목록 — 발송 상태 확인·재발송·PDF 미리보기. */}
          <button
            type="button"
            onClick={() => setSending(true)}
            className="inline-flex items-center gap-s-2 rounded-r-1 border border-line-hairline bg-surface-base px-s-3 py-s-3 lf-body-sm text-fg-2 transition-colors duration-base ease-standard hover:border-line-soft hover:text-fg-1 focus-visible:outline-none focus-visible:shadow-focus"
          >
            <FileText size={15} strokeWidth={1.6} />
            확인서 목록
          </button>
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="inline-flex items-center gap-s-2 rounded-r-1 border border-line-hairline bg-surface-base px-s-3 py-s-3 lf-body-sm text-fg-2 transition-colors duration-base ease-standard hover:border-line-soft hover:text-fg-1 focus-visible:outline-none focus-visible:shadow-focus"
          >
            <Plus size={15} strokeWidth={1.8} />
            판매상품 추가
          </button>
        </div>
      </div>

      {saveMsg ? (
        <div
          className={`mt-s-5 border-l-2 px-s-4 py-s-3 lf-body-sm ${
            saveMsg.tone === 'ok'
              ? 'border-l-brand-blue bg-surface-soft text-fg-1'
              : saveMsg.tone === 'warn'
                ? 'border-l-status-warning bg-surface-soft text-fg-1'
                : 'border-l-status-danger bg-surface-soft text-status-danger'
          }`}
        >
          ▪ {saveMsg.text}
        </div>
      ) : null}

      {/* 추가 예정(미리보기) */}
      {drafts.length ? (
        <section className="mt-s-6 border border-brand-pink/40 bg-pink-50/40 p-s-4">
          <div className="flex flex-wrap items-center justify-between gap-s-3">
            <p className="lf-eyebrow text-fg-2">
              ▪ 추가 예정 (미리보기) · <span className="lf-num">{drafts.length}</span>건
            </p>
            <button
              type="button"
              disabled={saving}
              onClick={() => setConfirming(true)}
              className="inline-flex items-center gap-s-2 rounded-r-1 bg-brand-pink px-s-4 py-s-2 lf-body-sm text-brand-brown transition-colors duration-base ease-standard hover:bg-pink-500 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving ? '생성 중…' : `Salesforce에 저장 (${drafts.length})`}
            </button>
          </div>
          <div className="mt-s-3 overflow-x-auto border border-line-hairline bg-surface-base">
            <table className="w-full border-separate border-spacing-0">
              <thead>
                <tr>
                  <Th>판매상품</Th>
                  <Th>브랜드 제품명</Th>
                  <Th>카테고리</Th>
                  <Th>크리에이터</Th>
                  <Th>판매단가</Th>
                  <Th>콘텐츠제작단가</Th>
                  <Th>운영관리비</Th>
                  <Th>선입금 정산</Th>
                  <Th>후입금 정산</Th>
                  <Th>업로드 일정</Th>
                  <Th> </Th>
                </tr>
              </thead>
              <tbody>
                {drafts.map((d) => (
                  <tr key={d.key}>
                    <Td className="text-fg-1">
                      <span className="block">{d.product.name}</span>
                      {d.department ? <span className="lf-caption text-fg-3">{d.department}</span> : null}
                    </Td>
                    <Td className="text-fg-2">{d.brandItem || '—'}</Td>
                    <Td className="text-fg-2">
                      {d.businessCategory || d.itemCategory
                        ? `${d.businessCategory || '—'} · ${d.itemCategory || '—'}`
                        : '—'}
                    </Td>
                    <Td className="text-fg-2">{d.creator?.name || '—'}</Td>
                    <Td className="lf-num text-fg-2">{won(d.unitPrice)}</Td>
                    <Td className="lf-num text-fg-2">{won(d.contentsFee)}</Td>
                    <Td className="lf-num text-fg-2">{won(d.pricePrj)}</Td>
                    <Td className="lf-num text-fg-2">{d.prepayDate || '—'}</Td>
                    <Td className="lf-num text-fg-2">{d.deferredDate || '—'}</Td>
                    <Td className="lf-num text-fg-2">{d.uploadDate || '—'}</Td>
                    <Td>
                      <button
                        type="button"
                        onClick={() => removeDraft(d.key)}
                        aria-label="제거"
                        className="inline-flex items-center justify-center rounded-r-1 border border-line-hairline p-s-1 text-fg-3 transition-colors duration-base ease-standard hover:border-line-soft hover:text-status-danger"
                      >
                        <Trash2 size={14} strokeWidth={1.6} />
                      </button>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {/* 판매상품 목록 (체크박스 선택 → PDF 만들기) */}
      <div className="mt-s-6">
        <p className="lf-eyebrow text-fg-3">
          ▪ 판매상품
          {!loading ? (
            <span className="lf-num">
              {' '}· {filtered.length}
              {filtered.length !== items.length ? `/${items.length}` : ''}건
              {checked.size ? ` · ${checked.size} 선택` : ''}
            </span>
          ) : null}
        </p>
        {error ? <p className="mt-s-3 lf-body-sm text-status-danger">▪ {error}</p> : null}
        <div className="mt-s-3 overflow-x-auto border border-line-hairline">
          <table className="w-full border-separate border-spacing-0">
            <thead>
              <tr>
                <th className="w-10 border-b border-line-soft bg-surface-soft px-s-3 py-s-2 text-left">
                  <input
                    type="checkbox"
                    aria-label="전체 선택"
                    checked={allChecked}
                    ref={(el) => {
                      if (el) el.indeterminate = someChecked
                    }}
                    disabled={filteredIds.length === 0}
                    onChange={toggleAll}
                    className="h-4 w-4 cursor-pointer accent-brand-pink disabled:cursor-not-allowed"
                  />
                </th>
                <Th>판매상품</Th>
                <Th>브랜드 제품명</Th>
                <Th>크리에이터</Th>
                <Th>판매단가</Th>
                <Th>콘텐츠제작단가</Th>
                <Th>운영관리비</Th>
                <Th>매출액</Th>
                <Th>선입금 정산</Th>
                <Th>후입금 정산</Th>
                <Th>업로드 일정</Th>
                <Th>확인서 발송</Th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <Td colSpan={12} className="text-center text-fg-3">
                    불러오는 중…
                  </Td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <Td colSpan={12} className="text-center text-fg-3">
                    아직 판매상품이 없어요. "판매상품 추가"로 담아보세요.
                  </Td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <Td colSpan={12} className="text-center text-fg-3">
                    필터에 맞는 판매상품이 없어요.
                  </Td>
                </tr>
              ) : (
                filtered.map((it) => (
                  <tr key={it.id} className={checked.has(it.id) ? 'bg-pink-50/40' : undefined}>
                    <Td>
                      <input
                        type="checkbox"
                        aria-label={`${it.product ?? ''} 선택`}
                        checked={checked.has(it.id)}
                        onChange={() => toggleOne(it.id)}
                        className="h-4 w-4 cursor-pointer accent-brand-pink"
                      />
                    </Td>
                    <Td className="text-fg-1">
                      <span className="block">{it.product || '—'}</span>
                      {it.department ? (
                        <span className="lf-caption text-fg-3">{it.department}</span>
                      ) : null}
                    </Td>
                    <Td className="text-fg-2">{it.brandItem || it.optionName || '—'}</Td>
                    <Td className="text-fg-2">
                      {it.creator ? (
                        it.creator
                      ) : (
                        <button
                          type="button"
                          onClick={() => setAssignFor(it)}
                          className="inline-flex items-center gap-s-1 rounded-r-1 border border-dashed border-line-strong bg-surface-base px-s-2 py-px lf-caption text-fg-2 transition-colors duration-base ease-standard hover:border-brand-pink hover:text-fg-1"
                        >
                          <Plus size={12} strokeWidth={1.8} />
                          크리에이터 지정
                        </button>
                      )}
                    </Td>
                    <Td className="lf-num text-fg-2">{wonN(it.unitPrice)}</Td>
                    <Td className="lf-num text-fg-2">{wonN(it.contentsFee)}</Td>
                    <Td className="lf-num text-fg-2">{wonN(it.pricePrj)}</Td>
                    <Td className="lf-num text-fg-2">{wonN(it.totalPrice)}</Td>
                    <Td className="lf-num text-fg-2">{it.prepayDate || '—'}</Td>
                    <Td className="lf-num text-fg-2">{it.deferredDate || '—'}</Td>
                    <Td className="lf-num text-fg-2">{it.uploadDate || '—'}</Td>
                    <Td>
                      <LineSendStatus item={it} quote={quoteOf(it)} loading={quotes === null} />
                    </Td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <p className="mt-s-4 flex items-start gap-s-2 lf-caption text-fg-3">
        <span aria-hidden>▪</span>
        <span>
          판매상품을 체크하고 <span className="text-fg-2">"PDF 만들기"</span>를 누르면 그 크리에이터들의 확인서 PDF를
          만들고 메일 발송까지 진행해요. 발송 상태·재발송은 <span className="text-fg-2">"확인서 목록"</span>에서 봐요. ("판매상품 추가"의 "추가 예정"은 아직 SF 미반영 미리보기예요.)
        </span>
      </p>
        </>
      )}

      {adding ? (
        <AddProductModal onClose={() => setAdding(false)} onAdd={addDrafts} />
      ) : null}

      {confirming ? (
        <ConfirmSaveModal
          count={drafts.length}
          oppName={opp.oppName}
          onCancel={() => setConfirming(false)}
          onConfirm={save}
        />
      ) : null}

      {assignFor ? (
        <AssignCreatorsModal
          opp={opp}
          item={assignFor}
          onClose={() => setAssignFor(null)}
          onDone={(_ids, notice) => {
            setSaveMsg({ tone: notice.ok ? 'ok' : 'warn', text: notice.text })
            void load()
          }}
        />
      ) : null}
    </div>
  )
}

/** 판매상품의 확인서 발송 상태 — 발송완료(+날짜) / 발송 중 / 실패 / 초안 / 미발송 / 확인서 없음. */
function LineSendStatus({ item, quote, loading }: { item: LineItemRow; quote: QuoteRow | null; loading: boolean }) {
  const chip = 'inline-flex w-fit whitespace-nowrap rounded-r-1 border px-s-2 py-px lf-caption'
  if (loading) return <span className="lf-caption text-fg-3">…</span>
  if (!item.creatorId) return <span className="lf-caption text-fg-3">—</span>
  if (!quote) return <span className="whitespace-nowrap lf-caption text-fg-3">확인서 없음</span>
  const m = quote.mail
  if (m?.status === 'sent' || quote.status === '전송완료') {
    return (
      <span className="inline-flex flex-col" title={m?.to ? `받는 사람: ${m.to}` : undefined}>
        <span className={`${chip} border-brand-blue text-brand-blue`}>발송완료</span>
        {m?.sentAt ? <span className="mt-px lf-num lf-caption whitespace-nowrap text-fg-3">{fmtDateTime(m.sentAt)}</span> : null}
      </span>
    )
  }
  if (m?.status === 'sending') return <span className={`${chip} border-line-soft text-fg-2`}>발송 중</span>
  if (m?.status === 'failed')
    return (
      <span title={m.error ?? undefined} className={`${chip} border-status-danger text-status-danger`}>
        발송 실패
      </span>
    )
  if (m?.status === 'drafted') return <span className={`${chip} border-line-soft text-fg-1`}>초안</span>
  return <span className={`${chip} border-dashed border-line-strong text-fg-3`}>미발송</span>
}

/** 필터 입력 한 칸. */
function FilterInput({
  label,
  value,
  onChange,
  placeholder,
  numeric,
  narrow,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  placeholder?: string
  numeric?: boolean
  narrow?: boolean
}) {
  return (
    <label className="block">
      <span className="lf-eyebrow text-fg-3">▪ {label}</span>
      <div className="relative mt-s-2">
        <span className="pointer-events-none absolute left-s-2 top-1/2 -translate-y-1/2 text-fg-3">
          <Search size={13} strokeWidth={1.6} />
        </span>
        <input
          type={numeric ? 'number' : 'text'}
          inputMode={numeric ? 'numeric' : undefined}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={`${narrow ? 'w-[140px]' : 'w-[180px]'} rounded-r-1 border border-line-soft bg-surface-base py-s-2 pl-s-7 pr-s-2 lf-body-sm text-fg-1 placeholder:text-fg-3 focus-visible:border-brand-blue focus-visible:outline-none focus-visible:shadow-focus`}
        />
      </div>
    </label>
  )
}

/** 프로덕션 Salesforce 쓰기 확인 — "생성"을 눌러야 실제 create. */
function ConfirmSaveModal({
  count,
  oppName,
  onCancel,
  onConfirm,
}: {
  count: number
  oppName: string
  onCancel: () => void
  onConfirm: () => void
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-brand-brown/40 p-s-5"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel()
      }}
    >
      <div className="mt-s-9 w-full max-w-[460px] border border-line-hairline bg-surface-base p-s-6 shadow-pop">
        <p className="lf-eyebrow text-fg-3">▪ Salesforce 저장 확인</p>
        <h3 className="mt-s-2 lf-h3 text-fg-1">판매상품 {count}건을 생성할까요?</h3>
        <p className="mt-s-3 lf-body-sm text-fg-2">
          <span className="text-fg-1">{oppName}</span> 기회에 <span className="lf-num">{count}</span>건이{' '}
          <span className="text-status-warning">프로덕션 Salesforce</span>에 실제로 생성돼요. 되돌리려면
          Salesforce에서 직접 삭제해야 해요.
        </p>
        <div className="mt-s-5 flex items-center justify-end gap-s-3">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-r-1 border border-line-hairline bg-surface-base px-s-4 py-s-2 lf-body-sm text-fg-2 transition-colors duration-base ease-standard hover:border-line-soft hover:text-fg-1"
          >
            취소
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="rounded-r-2 bg-brand-pink px-s-5 py-s-2 lf-h4 text-brand-brown transition-colors duration-base ease-standard hover:bg-pink-500"
          >
            생성 ({count})
          </button>
        </div>
      </div>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* PDF 만들기 + 발송 — 판매상품 체크 → 확인 화면 1장 → PDF 생성·SF 저장 → 발송    */
/* -------------------------------------------------------------------------- */

type PdfState = 'has' | 'pending' | 'running' | 'done' | 'failed'
type MailState = 'idle' | 'waiting' | 'sending' | 'sent' | 'failed'
type WizardPhase = 'review' | 'quote' | 'pdf' | 'pdfDone' | 'send' | 'done'

interface WizardTarget {
  quote: QuoteRow
  /** 체크한 판매상품 이름(같은 크리에이터의 여러 상품은 한 확인서로 묶임). */
  products: string[]
  email: string
  /** 첨부 PDF 파일명(확장자 제외) — 기본 규칙 값으로 채우고 수정 가능. */
  fileBase: string
  pdf: PdfState
  pdfError?: string
  /** 초안(제목·본문·받는 사람) 저장 성공 — 발송 대상. */
  draftOk: boolean
  mail: MailState
  mailError?: string
}

/** 확인서가 아직 없는 크리에이터 — 여기서 SF 에 확인서를 먼저 만든다. */
interface PendingQuote {
  creatorId: string
  creator: string
  products: string[]
  state: 'pending' | 'running'
}

interface WizardExcluded {
  key: string
  label: string
  reason: string
  /** 크리에이터가 없는 판매상품 — 여기서 바로 크리에이터 지정 가능. */
  assignable?: LineItemRow
}

/**
 * 체크한 판매상품 → 크리에이터별 확인서(Quote). 확인서 판매상품의 원본 OLI id 로 1:1 연결하고,
 * 없으면 같은 크리에이터의 확인서가 정확히 1건일 때만 연결한다. 발송 불가 건은 사유와 함께 제외.
 */
function buildWizardTargets(
  items: LineItemRow[],
  quotes: QuoteRow[],
  /** 기회 단계 — '제안'이면 SF 가 확인서 생성을 막는다(검증룰 Cant_Create_Quote). */
  stage: string | null,
): { targets: WizardTarget[]; pending: PendingQuote[]; excluded: WizardExcluded[] } {
  const byOli = new Map<string, QuoteRow>()
  const byCreator = new Map<string, QuoteRow[]>()
  for (const q of quotes) {
    for (const l of q.lines) if (l.oliId) byOli.set(l.oliId, q)
    if (q.creatorId) byCreator.set(q.creatorId, [...(byCreator.get(q.creatorId) ?? []), q])
  }
  const targets = new Map<string, WizardTarget>()
  const pending = new Map<string, PendingQuote>()
  const excluded: WizardExcluded[] = []
  for (const it of items) {
    const label = it.creator || it.product || '판매상품'
    if (!it.creatorId) {
      excluded.push({
        key: it.id,
        label: it.brandItem || it.product || label,
        reason: '크리에이터가 없는 판매상품',
        assignable: it,
      })
      continue
    }
    const sameCreator = byCreator.get(it.creatorId) ?? []
    const q = byOli.get(it.id) ?? (sameCreator.length === 1 ? sameCreator[0] : null)
    if (!q) {
      if (sameCreator.length > 1 || stage === '제안') {
        excluded.push({
          key: it.id,
          label,
          reason:
            sameCreator.length > 1
              ? '확인서가 여러 건 — 확인서 목록에서 처리'
              : "확인서 없음 — 기회 단계가 '제안'이라 확인서를 만들 수 없어요. SF에서 판매상품 컨펌 요청 후 진행해 주세요",
        })
        continue
      }
      // 확인서가 없으면 여기서 만든다(같은 크리에이터의 판매상품은 한 확인서로).
      const pq = pending.get(it.creatorId)
      const product = it.product ?? ''
      if (pq) {
        if (product && !pq.products.includes(product)) pq.products.push(product)
      } else {
        pending.set(it.creatorId, { creatorId: it.creatorId, creator: label, products: product ? [product] : [], state: 'pending' })
      }
      continue
    }
    if (q.status === '전송완료' || q.mail?.status === 'sent') {
      excluded.push({ key: it.id, label, reason: '이미 발송된 확인서' })
      continue
    }
    const prev = targets.get(q.id)
    const product = it.product ?? ''
    if (prev) {
      if (product && !prev.products.includes(product)) prev.products.push(product)
      continue
    }
    targets.set(q.id, {
      quote: q,
      products: product ? [product] : [],
      email: q.email ?? '',
      fileBase: stripPdf(q.mailFileName),
      pdf: q.hasPdf ? 'has' : 'pending',
      draftOk: false,
      mail: 'idle',
    })
  }
  return { targets: [...targets.values()], pending: [...pending.values()], excluded }
}

function SendWizard({
  opp,
  items,
  onBack,
  onOpenList,
}: {
  opp: OpportunityRow
  /** 체크한 판매상품. */
  items: LineItemRow[]
  onBack: () => void
  onOpenList: () => void
}) {
  const tpl = defaultTemplate(opp)
  const [loading, setLoading] = useState(true)
  const [loadErr, setLoadErr] = useState<string | null>(null)
  const [targets, setTargets] = useState<WizardTarget[]>([])
  const [excluded, setExcluded] = useState<WizardExcluded[]>([])
  const [subject, setSubject] = useState(tpl.subject)
  const [body, setBody] = useState(tpl.body)
  const [template, setTemplate] = useState<string | null>(null)
  const [tplErr, setTplErr] = useState<string | null>(null)
  const templates = usePdfTemplates(setTplErr)
  const mailStatus = useMailStatus()
  const mailReady = Boolean(mailStatus?.configured)
  const [phase, setPhase] = useState<WizardPhase>('review')
  const [confirmPdf, setConfirmPdf] = useState(false)
  const [toast, setToast] = useState<ToastMsg | null>(null)
  // 대상 판매상품 — 크리에이터 지정으로 판매상품이 바뀌거나 늘어나면 갱신해 다시 계산한다.
  const [wItems, setWItems] = useState<LineItemRow[]>(items)
  const [assignFor, setAssignFor] = useState<LineItemRow | null>(null)
  // 확인서 생성 대상(확인서 없는 크리에이터) + 생성 실패 사유(재계산해도 유지).
  const [pending, setPending] = useState<PendingQuote[]>([])
  const [quoteErrors, setQuoteErrors] = useState<Map<string, string>>(new Map())
  const [confirmQuote, setConfirmQuote] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null)
  const [previewKey, setPreviewKey] = useState<string | null>(null)
  // 첨부 파일명 일괄 변경 — 'Leferi_확인서_{크리에이터명}_' 뒤 부분을 전체 또는 선택한 확인서에 적용.
  const [fnMode, setFnMode] = useState<'all' | 'selected'>('all')
  const [fnSelected, setFnSelected] = useState<Set<string>>(new Set())
  const [fnSuffix, setFnSuffix] = useState<string | null>(null) // null = 기본값(첫 확인서 기준)

  // 크리에이터 지정 후 — 원본(수정됨)과 복제된 판매상품으로 대상을 바꾼다.
  const afterAssign = async (srcId: string, ids: string[], notice: { ok: boolean; text: string }) => {
    setToast({ type: notice.ok ? 'success' : 'error', title: notice.ok ? 'Assigned' : 'Partly assigned', message: notice.text })
    try {
      const fresh = await getOpportunityLineItems(opp.oppId)
      const want = new Set([srcId, ...ids])
      setWItems((prev) => [...prev.filter((x) => x.id !== srcId), ...fresh.rows.filter((x) => want.has(x.id))])
    } catch {
      /* 목록 갱신 실패 — 대상은 그대로 둔다 */
    }
  }

  useEffect(() => {
    let live = true
    setLoading(true)
    getOpportunityQuotes(opp.oppId, '전체')
      .then((r) => {
        if (!live) return
        const b = buildWizardTargets(wItems, r.rows, opp.stage)
        setTargets(b.targets)
        setPending(b.pending)
        setExcluded(b.excluded)
      })
      .catch((e: unknown) => {
        if (live) setLoadErr(e instanceof ConfirmationError ? humanizeConfirmationError(e.message) : '확인서 조회 실패')
      })
      .finally(() => live && setLoading(false))
    return () => {
      live = false
    }
  }, [opp.oppId, opp.stage, wItems, reloadKey])

  // 0단계 — 확인서 없는 크리에이터마다 SF 에 비즈니스 확인서 생성(상태 초안). ⚠️ SF 쓰기.
  const runQuotes = async () => {
    setConfirmQuote(false)
    setPhase('quote')
    setNotice(null)
    const errs = new Map(quoteErrors)
    let created = 0
    for (const pq of pending) {
      setPending((prev) => prev.map((x) => (x.creatorId === pq.creatorId ? { ...x, state: 'running' } : x)))
      try {
        const r = await createConfirmations(opp.oppId, [pq.creatorId])
        const res = r.results[0]
        if (res?.status === 'created' || res?.status === 'exists') {
          created++
          errs.delete(pq.creatorId)
          if (res.warning) errs.set(pq.creatorId, res.warning)
        } else errs.set(pq.creatorId, humanizeConfirmationError(res?.error ?? 'create_failed'))
      } catch (e) {
        errs.set(pq.creatorId, e instanceof ConfirmationError ? humanizeConfirmationError(e.message) : '확인서 생성 실패')
      }
      setPending((prev) => prev.map((x) => (x.creatorId === pq.creatorId ? { ...x, state: 'pending' } : x)))
    }
    setQuoteErrors(errs)
    const failed = pending.length - created
    setNotice({
      ok: failed === 0,
      text:
        `확인서 ${created}건을 Salesforce에 만들었어요(상태 초안)` +
        (failed ? `, ${failed}건 실패` : '') +
        (created ? '. 템플릿을 고른 뒤 "PDF 생성"으로 이어가세요.' : '. 실패 사유를 확인해 주세요.'),
    })
    setToast(
      failed === 0
        ? { type: 'success', title: 'Created', message: `확인서 ${created}건을 만들었어요.` }
        : { type: 'error', title: 'Partly created', message: `확인서 ${created}건 생성, ${failed}건 실패했어요.` },
    )
    setPhase('review')
    setReloadKey((k) => k + 1) // 새 확인서를 불러와 대상 재계산
  }

  const patch = (id: string, p: Partial<WizardTarget>) =>
    setTargets((prev) => prev.map((t) => (t.quote.id === id ? { ...t, ...p } : t)))

  const editable = phase === 'review'
  const needsPdf = targets.filter((t) => t.pdf === 'pending' || t.pdf === 'failed')
  // 아직 생성을 시도하지 않은 확인서(실패 사유가 없는 것).
  const untriedQuotes = pending.filter((pq) => !quoteErrors.has(pq.creatorId))

  // 미리보기 대상 — 생성 예정(판매상품 값으로) / PDF 있음(저장분) / PDF 없음(템플릿으로 생성될 모양).
  const previewOptions: { key: string; label: string; url: string | null; note?: string }[] = [
    ...pending.map((pq) => ({
      key: `c:${pq.creatorId}`,
      label: `${pq.creator} (확인서 생성 예정)`,
      url: template ? previewPdfUrl(opp.oppId, pq.creatorId, template) : null,
      note: '판매상품 값으로 그린 미리보기예요. 확인서를 만들면 SF 값으로 다시 만들어요.',
    })),
    ...targets.map((t) => {
      const has = t.pdf === 'has' || t.pdf === 'done'
      return {
        key: `q:${t.quote.id}`,
        label: `${t.quote.creator || t.quote.name}${has ? ' (기존 PDF)' : ''}`,
        url: has ? quotePdfUrl(t.quote.id) : template ? generatePdfUrl(t.quote.id, template) : null,
        note: has ? 'Salesforce에 저장된 PDF예요. 템플릿을 바꿔도 이 PDF가 그대로 첨부돼요.' : undefined,
      }
    }),
  ]
  const preview = previewOptions.find((o) => o.key === previewKey) ?? previewOptions[0] ?? null

  const fnDefaultSuffix = targets.length ? fileNameSuffix(stripPdf(targets[0].quote.mailFileName), targets[0].quote.creator) : ''
  const fnSuffixValue = fnSuffix ?? fnDefaultSuffix
  const fnScope = fnMode === 'all' ? targets : targets.filter((t) => fnSelected.has(t.quote.id))
  const applyFileNames = () => {
    const ids = new Set(fnScope.map((t) => t.quote.id))
    const suffix = fnSuffixValue.trim()
    setTargets((prev) =>
      prev.map((t) =>
        ids.has(t.quote.id)
          ? { ...t, fileBase: [fileNamePrefix(t.quote.creator), suffix].filter(Boolean).join('_') }
          : t,
      ),
    )
    setToast({ type: 'success', title: 'Renamed', message: `첨부 파일명 ${ids.size}건을 바꿨어요.` })
  }
  const fnShowSelect = editable && fnMode === 'selected'
  const tableCols = fnShowSelect ? 7 : 6
  const blockReason = !targets.length
    ? '확인서를 먼저 만들어 주세요.'
    : needsPdf.length && !template
      ? 'PDF가 없는 확인서가 있어요. 템플릿을 골라 주세요.'
      : targets.some((t) => !t.email.trim())
          ? '받는 사람 이메일이 비어 있는 확인서가 있어요.'
          : !subject.trim() || !body.trim()
            ? '제목과 본문을 입력해 주세요.'
            : null

  // 1단계 — 크리에이터별 PDF 준비(없으면 생성·SF 저장) + 메일 초안 저장. 메일은 아직 안 나감.
  const runPdf = async () => {
    setConfirmPdf(false)
    setPhase('pdf')
    for (const t of targets) {
      if (t.draftOk) continue
      patch(t.quote.id, { pdf: t.pdf === 'has' ? 'has' : 'running', pdfError: undefined })
      try {
        const r = await createDrafts(
          opp.oppId,
          [{ quoteId: t.quote.id, to: t.email.trim(), subject, body, fileName: t.fileBase.trim() }],
          t.pdf === 'has' ? null : template,
        )
        const res = r.results[0]
        if (res?.status === 'created') patch(t.quote.id, { pdf: res.generated ? 'done' : 'has', draftOk: true })
        else
          patch(t.quote.id, {
            pdf: t.pdf === 'has' ? 'has' : 'failed',
            pdfError: humanizeConfirmationError(res?.error ?? 'draft_failed'),
          })
      } catch (e) {
        patch(t.quote.id, {
          pdf: t.pdf === 'has' ? 'has' : 'failed',
          pdfError: e instanceof ConfirmationError ? humanizeConfirmationError(e.message) : 'PDF 준비 실패',
        })
      }
    }
    setPhase('pdfDone')
  }

  // 2단계 — 준비된 건을 1통씩 발송. ⚠️ 실제 발송.
  const runSend = async (only?: Set<string>) => {
    const queue = targets.filter((t) => t.draftOk && t.mail !== 'sent' && (!only || only.has(t.quote.id)))
    if (!queue.length) return
    setPhase('send')
    for (const t of queue) patch(t.quote.id, { mail: 'waiting', mailError: undefined })
    let sent = 0
    let failed = 0
    for (const t of queue) {
      patch(t.quote.id, { mail: 'sending' })
      try {
        const r = await sendDrafts([t.quote.id])
        const res = r.results[0]
        if (res?.status === 'sent') {
          sent++
          patch(t.quote.id, { mail: 'sent' })
        } else {
          failed++
          patch(t.quote.id, { mail: 'failed', mailError: humanizeConfirmationError(res?.error ?? 'send_failed') })
        }
      } catch (e) {
        failed++
        patch(t.quote.id, {
          mail: 'failed',
          mailError: e instanceof ConfirmationError ? humanizeConfirmationError(e.message) : '발송 실패',
        })
      }
    }
    setPhase('done')
    setToast(
      failed === 0
        ? { type: 'success', title: 'Sent', message: `${sent}건 발송을 완료했어요.` }
        : sent === 0
          ? { type: 'error', title: 'Send failed', message: `${failed}건 모두 발송하지 못했어요. 사유를 확인해 주세요.` }
          : { type: 'error', title: 'Partly sent', message: `${sent}건 발송, ${failed}건 실패했어요.` },
    )
  }

  // PDF 단계 후 내용을 고치려면 처음으로 — 이미 만든 PDF 는 그대로 쓰고 초안만 다시 저장한다.
  const backToEdit = () => {
    setTargets((prev) =>
      prev.map((t) => ({
        ...t,
        pdf: t.pdf === 'done' || t.pdf === 'has' ? 'has' : 'pending',
        pdfError: undefined,
        draftOk: false,
        mail: 'idle',
        mailError: undefined,
      })),
    )
    setPhase('review')
  }

  const ready = targets.filter((t) => t.draftOk)
  const pdfDoneCount = targets.filter((t) => t.draftOk || t.pdf === 'failed' || t.pdfError).length
  const mailDoneCount = targets.filter((t) => t.mail === 'sent' || t.mail === 'failed').length
  const mailQueue = targets.filter((t) => t.mail !== 'idle').length
  const failedSend = targets.filter((t) => t.mail === 'failed')
  const sentCount = targets.filter((t) => t.mail === 'sent').length
  const pink =
    'inline-flex items-center gap-s-2 rounded-r-2 bg-brand-pink px-s-5 py-s-3 lf-h4 text-brand-brown transition-colors duration-base ease-standard hover:bg-pink-500 disabled:cursor-not-allowed disabled:opacity-50'
  const outline =
    'inline-flex items-center gap-s-2 rounded-r-2 border border-line-soft bg-surface-base px-s-4 py-s-3 lf-h4 text-fg-1 transition-colors duration-base ease-standard hover:border-line-strong hover:bg-surface-soft disabled:cursor-not-allowed disabled:opacity-50'
  const inputCls =
    'mt-s-2 w-full rounded-r-1 border border-line-soft bg-surface-base px-s-3 py-s-2 lf-body-sm text-fg-1 focus-visible:border-brand-blue focus-visible:outline-none focus-visible:shadow-focus disabled:cursor-not-allowed disabled:bg-surface-soft disabled:text-fg-2'

  return (
    <div className="mt-s-5">
      {/* 상단 바 — 단계별 주 버튼 */}
      <div className="flex flex-wrap items-center justify-between gap-s-3 border border-line-hairline bg-surface-soft px-s-4 py-s-3">
        <div className="flex flex-wrap items-center gap-s-3">
          <button
            type="button"
            onClick={onBack}
            disabled={phase === 'pdf' || phase === 'send'}
            className="inline-flex items-center gap-s-2 rounded-r-1 border border-line-hairline bg-surface-base px-s-3 py-s-2 lf-body-sm text-fg-2 transition-colors duration-base ease-standard hover:border-line-soft hover:text-fg-1 disabled:opacity-50"
          >
            <ChevronLeft size={14} strokeWidth={1.5} />
            판매상품
          </button>
          <p className="lf-body-sm text-fg-2">
            확인서 <span className="lf-num text-fg-1">{targets.length}</span>건
            {pending.length ? <span className="text-fg-1"> · 생성 예정 {pending.length}건</span> : null}
            {excluded.length ? <span className="text-fg-3"> · 제외 {excluded.length}건</span> : null}
            <span className="text-fg-3"> · 발신 {mailStatus?.from ?? LECOMMEND_FROM}</span>
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-s-3">
          {/* 확인서 생성 — 아직 시도 안 한 건이 있으면 주 버튼, 실패만 남았으면 보조(다시 생성) */}
          {phase === 'review' && pending.length ? (
            <button
              type="button"
              className={untriedQuotes.length ? pink : outline}
              disabled={loading}
              onClick={() => setConfirmQuote(true)}
            >
              <Plus size={15} strokeWidth={1.8} />
              {untriedQuotes.length ? `확인서 ${pending.length}건 생성` : `실패 ${pending.length}건 다시 생성`}
            </button>
          ) : null}
          {/* PDF 생성 — 확인서가 있는 건은 다른 건의 생성 여부와 상관없이 진행 가능 */}
          {phase === 'review' && targets.length ? (
            <button
              type="button"
              className={untriedQuotes.length ? outline : pink}
              disabled={loading || Boolean(blockReason)}
              title={blockReason ?? undefined}
              onClick={() => setConfirmPdf(true)}
            >
              <FileCheck2 size={15} strokeWidth={1.6} />
              {targets.length}건 PDF 생성
            </button>
          ) : null}
          {phase === 'quote' ? (
            <button type="button" className={pink} disabled>
              확인서 생성 중…
            </button>
          ) : null}
          {phase === 'pdf' ? (
            <button type="button" className={pink} disabled>
              PDF 준비 중… {pdfDoneCount}/{targets.length}
            </button>
          ) : null}
          {phase === 'pdfDone' ? (
            <>
              <button type="button" className={outline} onClick={backToEdit}>
                다시 편집
              </button>
              <button
                type="button"
                className={pink}
                disabled={!ready.length || !mailReady}
                title={!mailReady ? '메일 발송(SMTP)이 서버에 아직 설정되지 않았어요.' : undefined}
                onClick={() => runSend()}
              >
                <Send size={15} strokeWidth={1.6} />
                {`${ready.length}명에게 발송`}
              </button>
            </>
          ) : null}
          {phase === 'send' ? (
            <button type="button" className={pink} disabled>
              발송 중… {mailDoneCount}/{mailQueue}
            </button>
          ) : null}
          {phase === 'done' ? (
            <>
              <button type="button" className={outline} onClick={onOpenList}>
                확인서 목록
              </button>
              {failedSend.length ? (
                <button
                  type="button"
                  className={pink}
                  disabled={!mailReady}
                  onClick={() => runSend(new Set(failedSend.map((t) => t.quote.id)))}
                >
                  <Send size={15} strokeWidth={1.6} />
                  실패 {failedSend.length}건 다시 발송
                </button>
              ) : null}
            </>
          ) : null}
        </div>
      </div>

      {/* 단계 표시 + 진행 막대 */}
      <WizardSteps phase={phase} pending={untriedQuotes.length > 0 || (pending.length > 0 && !targets.length)} />
      {/* 다음 단계 버튼이 막힌 이유를 눈에 보이게 */}
      {phase === 'review' && targets.length && blockReason ? (
        <p className="mt-s-2 lf-caption text-status-warning">▪ PDF 생성 전에: {blockReason}</p>
      ) : null}
      {phase === 'pdf' || phase === 'send' ? (
        <div className="mt-s-3 h-[4px] w-full bg-surface-soft" role="progressbar" aria-valuemin={0} aria-valuemax={100}>
          <div
            className="h-full bg-brand-pink transition-[width] duration-base ease-standard"
            style={{
              width: `${
                phase === 'pdf'
                  ? (pdfDoneCount / Math.max(1, targets.length)) * 100
                  : (mailDoneCount / Math.max(1, mailQueue)) * 100
              }%`,
            }}
          />
        </div>
      ) : null}

      {loadErr ? <ResultBanner ok={false} text={loadErr} /> : null}
      {notice && phase === 'review' ? <ResultBanner ok={notice.ok} text={notice.text} /> : null}

      {/* 확인서 생성 — 확인서가 없는 크리에이터 */}
      {pending.length ? (
        <section className="mt-s-4 border border-line-hairline">
          <div className="border-b border-line-hairline px-s-5 py-s-4">
            <p className="lf-h4 text-fg-1">확인서 생성 {pending.length}건</p>
            <p className="mt-s-1 lf-caption text-fg-3">
              Salesforce에 크리에이터별 비즈니스 확인서를 만들어요(상태 초안). 크리에이터 사업자·계좌, 클라이언트 정보, 정산일,
              확인서 이름은 SF가 자동으로 채워요. 만든 뒤 PDF 생성 → 발송으로 이어져요.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full border-separate border-spacing-0">
              <thead>
                <tr>
                  <Th>크리에이터</Th>
                  <Th>판매상품</Th>
                  <Th>확인서</Th>
                </tr>
              </thead>
              <tbody>
                {pending.map((pq) => {
                  const err = quoteErrors.get(pq.creatorId)
                  return (
                    <tr key={pq.creatorId}>
                      <Td className="text-fg-1">{pq.creator}</Td>
                      <Td className="text-fg-2">
                        <span className="flex flex-col gap-px">
                          {(pq.products.length ? pq.products : ['—']).map((x) => (
                            <span key={x}>{x}</span>
                          ))}
                        </span>
                      </Td>
                      <Td>
                        {pq.state === 'running' ? (
                          <span className="inline-flex w-fit whitespace-nowrap rounded-r-1 border border-line-soft px-s-2 py-px lf-caption text-fg-1">
                            생성 중…
                          </span>
                        ) : err ? (
                          <span className="flex flex-col gap-px">
                            <span className="inline-flex w-fit whitespace-nowrap rounded-r-1 border border-status-danger px-s-2 py-px lf-caption text-status-danger">
                              실패
                            </span>
                            <span className="max-w-[320px] whitespace-normal lf-caption text-status-danger">{err}</span>
                          </span>
                        ) : (
                          <span className="inline-flex w-fit whitespace-nowrap rounded-r-1 border border-dashed border-line-strong px-s-2 py-px lf-caption text-fg-3">
                            생성 예정
                          </span>
                        )}
                      </Td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
      {mailStatus && !mailReady ? (
        <ResultBanner ok={false} text="메일 발송(SMTP)이 서버에 아직 설정되지 않았어요. PDF 생성까지만 되고 발송은 막혀요." />
      ) : null}
      {phase === 'pdfDone' ? (
        <ResultBanner
          ok={ready.length === targets.length}
          text={
            `PDF 준비 ${ready.length}건 완료` +
            (targets.length - ready.length ? `, ${targets.length - ready.length}건 실패` : '') +
            '. 아래 결과를 확인한 뒤 "발송"을 눌러 주세요.'
          }
        />
      ) : null}
      {phase === 'done' ? (
        <ResultBanner
          ok={!failedSend.length}
          text={`${sentCount}건 발송 완료${failedSend.length ? ` · ${failedSend.length}건 실패` : ''}.`}
        />
      ) : null}

      {/* 확인서 PDF — 템플릿 선택 + 미리보기(확인서 생성 전에도) */}
      {targets.length || pending.length ? (
        <section className="mt-s-4 border border-line-hairline">
          <div className="border-b border-line-hairline px-s-5 py-s-4">
            <p className="lf-h4 text-fg-1">확인서 PDF · 템플릿</p>
            <p className="mt-s-1 lf-caption text-fg-3">
              PDF가 없는 확인서는 고른 템플릿으로 만들어 Salesforce에 저장해요. 이미 PDF가 있는 확인서는 그대로 써요. 오른쪽에서
              크리에이터를 바꿔 가며 미리 볼 수 있어요.
            </p>
            {tplErr ? <p className="mt-s-2 lf-caption text-status-danger">▪ {tplErr}</p> : null}
          </div>
          <div className="flex flex-col md:h-[640px] md:flex-row">
            <TemplatePicker
              templates={templates}
              loading={templates === null && !tplErr}
              value={template}
              disabled={!editable}
              onChange={setTemplate}
            />
            <div className="flex min-h-[480px] min-w-0 flex-1 flex-col">
              <div className="flex flex-wrap items-center gap-s-3 border-b border-line-hairline px-s-4 py-s-3">
                <label className="flex items-center gap-s-2 lf-caption text-fg-3">
                  미리보기
                  <select
                    value={preview?.key ?? ''}
                    onChange={(e) => setPreviewKey(e.target.value)}
                    className="rounded-r-1 border border-line-soft bg-surface-base px-s-2 py-s-1 lf-body-sm text-fg-1 focus-visible:border-brand-blue focus-visible:outline-none focus-visible:shadow-focus"
                  >
                    {previewOptions.map((o) => (
                      <option key={o.key} value={o.key}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>
                {preview?.note ? <span className="lf-caption text-fg-3">▪ {preview.note}</span> : null}
              </div>
              {preview?.url ? (
                <iframe key={preview.url} title="확인서 PDF 미리보기" src={preview.url} className="min-h-0 flex-1 border-0 bg-surface-soft" />
              ) : (
                <div className="flex flex-1 items-center justify-center bg-surface-soft px-s-5">
                  <p className="text-center lf-body-sm text-fg-3">왼쪽에서 템플릿을 고르면 미리보기가 떠요.</p>
                </div>
              )}
            </div>
          </div>
        </section>
      ) : null}

      {/* 받는 사람 · 첨부 · 진행 상황 */}
      <section className="mt-s-4 border border-line-hairline">
        <p className="border-b border-line-hairline px-s-5 py-s-4 lf-h4 text-fg-1">받는 사람 · 첨부</p>
        {targets.length && editable ? (
          <div className="flex flex-col gap-s-3 border-b border-line-hairline bg-surface-soft px-s-5 py-s-4">
            <p className="lf-eyebrow text-fg-3">▪ 첨부 파일명 바꾸기</p>
            <div className="flex flex-wrap items-center gap-s-2">
              <span className="lf-caption text-fg-2">Leferi_확인서_{'{크리에이터명}'}_</span>
              <input
                type="text"
                value={fnSuffixValue}
                onChange={(e) => setFnSuffix(e.target.value)}
                aria-label="크리에이터명 뒤 파일명"
                placeholder="예: 에이지투웨니스&아로셀 레코박스_10월"
                className="min-w-[280px] flex-1 rounded-r-1 border border-line-soft bg-surface-base px-s-2 py-s-1 lf-body-sm text-fg-1 placeholder:text-fg-3 focus-visible:border-brand-blue focus-visible:outline-none focus-visible:shadow-focus"
              />
              <span className="lf-caption text-fg-3">.pdf</span>
            </div>
            <div className="flex flex-wrap items-center gap-x-s-4 gap-y-s-2">
              <span className="lf-caption text-fg-3">적용 대상</span>
              <label className="inline-flex cursor-pointer items-center gap-s-1 lf-body-sm text-fg-1">
                <input
                  type="radio"
                  name="fn-mode"
                  checked={fnMode === 'all'}
                  onChange={() => setFnMode('all')}
                  className="h-4 w-4 accent-brand-pink"
                />
                전체 {targets.length}건 (일괄)
              </label>
              <label className="inline-flex cursor-pointer items-center gap-s-1 lf-body-sm text-fg-1">
                <input
                  type="radio"
                  name="fn-mode"
                  checked={fnMode === 'selected'}
                  onChange={() => setFnMode('selected')}
                  className="h-4 w-4 accent-brand-pink"
                />
                선택한 확인서 {fnSelected.size}건 (개별 선택)
              </label>
              <button
                type="button"
                disabled={!fnScope.length}
                onClick={applyFileNames}
                className="inline-flex items-center gap-s-1 rounded-r-1 border border-line-soft bg-surface-base px-s-3 py-s-1 lf-body-sm text-fg-1 transition-colors duration-base ease-standard hover:border-line-strong disabled:cursor-not-allowed disabled:opacity-50"
              >
                {fnScope.length}건에 적용
              </button>
              {fnMode === 'selected' ? (
                <span className="lf-caption text-fg-3">표 왼쪽 체크박스로 고를 확인서를 선택하세요.</span>
              ) : null}
            </div>
          </div>
        ) : null}
        <div className="overflow-x-auto">
          <table className="w-full border-separate border-spacing-0">
            <thead>
              <tr>
                {fnShowSelect ? (
                  <th className="w-10 border-b border-line-soft bg-surface-soft px-s-3 py-s-2 text-left">
                    <input
                      type="checkbox"
                      aria-label="파일명 바꿀 확인서 전체 선택"
                      checked={targets.length > 0 && targets.every((t) => fnSelected.has(t.quote.id))}
                      onChange={(e) =>
                        setFnSelected(e.target.checked ? new Set(targets.map((t) => t.quote.id)) : new Set())
                      }
                      className="h-4 w-4 cursor-pointer accent-brand-pink"
                    />
                  </th>
                ) : null}
                <Th>크리에이터</Th>
                <Th>판매상품</Th>
                <Th>받는 사람</Th>
                <Th>첨부</Th>
                <Th>PDF</Th>
                <Th>발송</Th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <Td colSpan={tableCols} className="text-center text-fg-3">
                    불러오는 중…
                  </Td>
                </tr>
              ) : targets.length === 0 ? (
                <tr>
                  <Td colSpan={tableCols} className="text-center text-fg-3">
                    {pending.length
                      ? '위에서 확인서를 먼저 만들어 주세요. 만들어지면 여기에 받는 사람·첨부가 나와요.'
                      : '보낼 확인서가 없어요. 아래 제외 사유를 확인해 주세요.'}
                  </Td>
                </tr>
              ) : (
                targets.map((t) => (
                  <tr key={t.quote.id}>
                    {fnShowSelect ? (
                      <Td>
                        <input
                          type="checkbox"
                          aria-label={`${t.quote.creator ?? ''} 파일명 바꿀 대상`}
                          checked={fnSelected.has(t.quote.id)}
                          onChange={() =>
                            setFnSelected((prev) => {
                              const n = new Set(prev)
                              if (n.has(t.quote.id)) n.delete(t.quote.id)
                              else n.add(t.quote.id)
                              return n
                            })
                          }
                          className="h-4 w-4 cursor-pointer accent-brand-pink"
                        />
                      </Td>
                    ) : null}
                    <Td className="text-fg-1">
                      <span className="block">{t.quote.creator || t.quote.name}</span>
                      <span className="lf-num lf-caption text-fg-3">{t.quote.quoteNumber}</span>
                    </Td>
                    <Td className="text-fg-2">
                      <span className="flex flex-col gap-px">
                        {(t.products.length ? t.products : ['—']).map((p) => (
                          <span key={p}>{p}</span>
                        ))}
                      </span>
                    </Td>
                    <Td>
                      <input
                        type="email"
                        value={t.email}
                        disabled={!editable}
                        onChange={(e) => patch(t.quote.id, { email: e.target.value })}
                        placeholder="수신자 이메일"
                        aria-label={`${t.quote.creator ?? ''} 받는 사람 이메일`}
                        className="w-[240px] rounded-r-1 border border-line-soft bg-surface-base px-s-3 py-s-2 lf-num lf-body-sm text-fg-1 placeholder:text-fg-3 focus-visible:border-brand-blue focus-visible:outline-none focus-visible:shadow-focus disabled:cursor-not-allowed disabled:bg-surface-soft disabled:text-fg-2"
                      />
                    </Td>
                    <Td>
                      <span className="flex flex-col gap-px">
                        <FileNameInput
                          value={t.fileBase}
                          defaultValue={stripPdf(t.quote.mailFileName)}
                          disabled={!editable}
                          label={`${t.quote.creator ?? ''} 첨부 파일명`}
                          onChange={(v) => patch(t.quote.id, { fileBase: v })}
                        />
                        {t.pdf === 'has' || t.pdf === 'done' ? (
                          <button
                            type="button"
                            onClick={() => window.open(quotePdfUrl(t.quote.id), '_blank', 'noopener')}
                            className="inline-flex w-fit items-center gap-s-1 lf-caption text-brand-blue underline-offset-2 hover:underline"
                          >
                            <Eye size={12} strokeWidth={1.6} />
                            PDF 보기
                          </button>
                        ) : template ? (
                          <button
                            type="button"
                            onClick={() => window.open(generatePdfUrl(t.quote.id, template), '_blank', 'noopener')}
                            className="inline-flex w-fit items-center gap-s-1 lf-caption text-brand-blue underline-offset-2 hover:underline"
                          >
                            <Eye size={12} strokeWidth={1.6} />
                            생성될 PDF 미리보기
                          </button>
                        ) : null}
                      </span>
                    </Td>
                    <Td>
                      <WizardPdfChip t={t} />
                    </Td>
                    <Td>
                      <WizardMailChip t={t} />
                    </Td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {excluded.length ? (
        <section className="mt-s-4 border border-line-hairline px-s-5 py-s-4">
          <p className="lf-h4 text-fg-1">제외 {excluded.length}건</p>
          <ul className="mt-s-2 flex flex-col gap-s-1">
            {excluded.map((x) => (
              <li key={x.key} className="flex flex-wrap items-center gap-x-s-3 gap-y-s-1 lf-body-sm">
                <span className="text-fg-1">{x.label}</span>
                <span className="text-fg-3">▪ {x.reason}</span>
                {x.assignable && editable ? (
                  <button
                    type="button"
                    onClick={() => setAssignFor(x.assignable!)}
                    className="inline-flex items-center gap-s-1 rounded-r-1 border border-dashed border-line-strong bg-surface-base px-s-2 py-px lf-caption text-fg-2 transition-colors duration-base ease-standard hover:border-brand-pink hover:text-fg-1"
                  >
                    <Plus size={12} strokeWidth={1.8} />
                    크리에이터 지정
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {/* 메일 내용 */}
      <section className="mt-s-4 border border-line-hairline p-s-5">
        <p className="lf-h4 text-fg-1">메일 내용</p>
        <p className="mt-s-1 lf-caption text-fg-3">모든 받는 사람에게 같은 제목·본문이 나가요. 첨부는 크리에이터별 확인서예요.</p>
        <div className="mt-s-4 flex flex-col gap-s-3">
          <label className="block">
            <span className="lf-eyebrow text-fg-3">▪ 보내는 사람</span>
            <input
              type="email"
              value={mailStatus ? `${mailStatus.fromName} <${mailStatus.from}>` : LECOMMEND_FROM}
              disabled
              readOnly
              className="mt-s-2 w-full cursor-not-allowed rounded-r-1 border border-line-hairline bg-surface-soft px-s-3 py-s-2 lf-num lf-body-sm text-fg-2"
            />
          </label>
          <label className="block">
            <span className="lf-eyebrow text-fg-3">▪ 제목</span>
            <input
              type="text"
              value={subject}
              disabled={!editable}
              onChange={(e) => setSubject(e.target.value)}
              className={inputCls}
            />
          </label>
          <label className="block">
            <span className="lf-eyebrow text-fg-3">▪ 본문</span>
            <textarea
              value={body}
              disabled={!editable}
              onChange={(e) => setBody(e.target.value)}
              rows={15}
              className={`${inputCls} resize-y leading-relaxed`}
            />
          </label>
          {editable ? (
            <div>
              <button
                type="button"
                onClick={() => {
                  setSubject(tpl.subject)
                  setBody(tpl.body)
                }}
                className="rounded-full border border-line-hairline bg-surface-base px-s-3 py-px lf-caption text-fg-2 transition-colors duration-base ease-standard hover:border-line-soft hover:text-fg-1"
              >
                기본 템플릿으로 되돌리기
              </button>
            </div>
          ) : (
            <p className="lf-caption text-fg-3">PDF를 준비한 뒤에는 내용이 잠겨요. 고치려면 "다시 편집"을 눌러 주세요.</p>
          )}
        </div>
      </section>

      {confirmQuote ? (
        <WizardConfirmModal
          eyebrow="확인서 생성"
          title={`Salesforce에 확인서 ${pending.length}건을 만들까요?`}
          lines={[
            `크리에이터별 비즈니스 확인서를 상태 "초안"으로 만들고, 그 크리에이터의 판매상품을 연결해요.`,
            '크리에이터 사업자·계좌, 클라이언트 정보, 정산일, 확인서 이름은 SF가 자동으로 채워요.',
            '만든 확인서는 SF에서 확인·승인할 수 있고, 이어서 PDF 생성과 발송을 진행해요.',
          ]}
          confirmLabel={`확인서 ${pending.length}건 생성`}
          onCancel={() => setConfirmQuote(false)}
          onConfirm={runQuotes}
        />
      ) : null}

      {confirmPdf ? (
        <WizardConfirmModal
          eyebrow="PDF 생성"
          title={`확인서 ${targets.length}건의 PDF를 준비할까요?`}
          lines={[
            needsPdf.length
              ? `PDF가 없는 ${needsPdf.length}건은 "${templateSubtitleC(template)}" 템플릿으로 만들어 Salesforce 확인서에 저장해요.`
              : '모든 확인서에 이미 PDF가 있어요. 그대로 첨부해요.',
            targets.length - needsPdf.length && needsPdf.length
              ? `이미 PDF가 있는 ${targets.length - needsPdf.length}건은 그대로 써요.`
              : '',
            '메일은 아직 안 나가요. 결과를 확인한 뒤 "발송"을 누르면 보내져요.',
          ].filter(Boolean)}
          confirmLabel={`${targets.length}건 PDF 생성`}
          onCancel={() => setConfirmPdf(false)}
          onConfirm={runPdf}
        />
      ) : null}

      {assignFor ? (
        <AssignCreatorsModal
          opp={opp}
          item={assignFor}
          onClose={() => setAssignFor(null)}
          onDone={(ids, notice) => void afterAssign(assignFor.id, ids, notice)}
        />
      ) : null}

      {toast ? <ToastView toast={toast} onClose={() => setToast(null)} /> : null}
    </div>
  )
}

/** 단계 표시 — 확인 → PDF 생성 → 발송. */
function WizardSteps({ phase, pending }: { phase: WizardPhase; pending: boolean }) {
  // ① 확인서 생성(없는 경우) ② PDF 생성 ③ 메일 발송.
  const idx = phase === 'quote' || (phase === 'review' && pending) ? 0 : phase === 'send' || phase === 'done' ? 2 : 1
  const steps = ['확인서 생성', 'PDF 생성 · SF 저장', '메일 발송']
  return (
    <ol className="mt-s-4 flex flex-wrap items-center gap-x-s-4 gap-y-s-1 lf-caption">
      {steps.map((s, i) => (
        <li key={s} className={`inline-flex items-center gap-s-2 ${i === idx ? 'text-fg-1' : i < idx ? 'text-fg-2' : 'text-fg-3'}`}>
          <span
            className={`inline-flex h-5 w-5 items-center justify-center rounded-full border lf-num ${
              i === idx ? 'border-brand-pink bg-brand-pink text-brand-brown' : 'border-line-soft'
            }`}
          >
            {i + 1}
          </span>
          {s}
          {i < steps.length - 1 ? <ChevronRight size={12} strokeWidth={1.6} className="text-fg-3" /> : null}
        </li>
      ))}
    </ol>
  )
}

function WizardPdfChip({ t }: { t: WizardTarget }) {
  const chip = 'inline-flex w-fit whitespace-nowrap rounded-r-1 border px-s-2 py-px lf-caption'
  if (t.pdfError) {
    return (
      <span className="flex flex-col gap-px">
        <span className={`${chip} border-status-danger text-status-danger`}>실패</span>
        <span className="max-w-[220px] whitespace-normal lf-caption text-status-danger">{t.pdfError}</span>
      </span>
    )
  }
  if (t.pdf === 'running') return <span className={`${chip} border-line-soft text-fg-1`}>생성 중…</span>
  if (t.pdf === 'done') return <span className={`${chip} border-brand-blue text-brand-blue`}>생성·저장됨</span>
  if (t.pdf === 'has') return <span className={`${chip} border-line-soft text-fg-2`}>{t.draftOk ? '준비됨' : '기존 PDF'}</span>
  return <span className={`${chip} border-dashed border-line-strong text-fg-3`}>생성 예정</span>
}

function WizardMailChip({ t }: { t: WizardTarget }) {
  const chip = 'inline-flex w-fit whitespace-nowrap rounded-r-1 border px-s-2 py-px lf-caption'
  if (t.mail === 'sent') return <span className={`${chip} border-brand-blue text-brand-blue`}>발송 완료</span>
  if (t.mail === 'sending') return <span className={`${chip} border-line-soft text-fg-1`}>발송 중…</span>
  if (t.mail === 'waiting') return <span className={`${chip} border-line-soft text-fg-3`}>대기</span>
  if (t.mail === 'failed') {
    return (
      <span className="flex flex-col gap-px">
        <span className={`${chip} border-status-danger text-status-danger`}>실패</span>
        <span className="max-w-[220px] whitespace-normal lf-caption text-status-danger">{t.mailError}</span>
      </span>
    )
  }
  return <span className="lf-caption text-fg-3">—</span>
}

/** 단순 확인창(취소 / 확인). */
function WizardConfirmModal({
  eyebrow,
  title,
  lines,
  confirmLabel,
  onCancel,
  onConfirm,
}: {
  eyebrow: string
  title: string
  lines: string[]
  confirmLabel: string
  onCancel: () => void
  onConfirm: () => void
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-brand-brown/40 p-s-5"
      role="dialog"
      aria-modal="true"
      aria-labelledby="wizard-confirm-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel()
      }}
    >
      <div className="mt-s-8 w-full max-w-[520px] border border-line-hairline bg-surface-base shadow-pop">
        <div className="px-s-5 py-s-4">
          <p className="lf-eyebrow text-fg-3">▪ {eyebrow}</p>
          <h3 id="wizard-confirm-title" className="mt-s-1 lf-h3 text-fg-1">
            {title}
          </h3>
          <ul className="mt-s-3 flex flex-col gap-s-2">
            {lines.map((l) => (
              <li key={l} className="lf-body-sm text-fg-2">
                {l}
              </li>
            ))}
          </ul>
        </div>
        <div className="flex items-center justify-end gap-s-3 border-t border-line-hairline px-s-5 py-s-4">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-r-1 border border-line-hairline bg-surface-base px-s-4 py-s-2 lf-body-sm text-fg-2 transition-colors duration-base ease-standard hover:border-line-soft hover:text-fg-1"
          >
            취소
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="inline-flex items-center gap-s-2 rounded-r-2 bg-brand-pink px-s-5 py-s-2 lf-h4 text-brand-brown transition-colors duration-base ease-standard hover:bg-pink-500"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}

export interface ToastMsg {
  type: 'success' | 'error'
  /** 영문 상황 단어(LCMD 토스트 규칙). */
  title: string
  /** 한글 설명. */
  message: string
}

/**
 * 토스트 — LCMD .ltst 규격 미러링: 하단 우측, 영문 제목 + 한글 설명, 자동 사라짐(6초), 닫기.
 * success = 핑크 그라디언트 + 브라운 잉크, error = 브라운 + 페이퍼 잉크.
 */
export function ToastView({ toast, onClose }: { toast: ToastMsg; onClose: () => void }) {
  // 부모가 다시 그려져도 타이머가 리셋되지 않게 onClose 는 ref 로.
  const closeRef = useRef(onClose)
  closeRef.current = onClose
  useEffect(() => {
    const t = setTimeout(() => closeRef.current(), 6000)
    return () => clearTimeout(t)
  }, [toast])
  const ok = toast.type === 'success'
  return (
    <div className="pointer-events-none fixed bottom-0 right-0 z-[60] flex max-w-full p-s-5" role="status" aria-live="polite">
      <div
        className={`pointer-events-auto flex w-[360px] max-w-[calc(100vw-40px)] items-start gap-s-3 rounded-r-2 border px-s-4 py-s-3 shadow-pop ${
          ok ? 'border-line-hairline bg-grad-pink-paper text-brand-brown' : 'border-transparent bg-brand-brown text-fg-inverse'
        }`}
      >
        <span className="mt-px shrink-0">
          {ok ? <CheckCircle2 size={20} strokeWidth={1.8} /> : <AlertCircle size={20} strokeWidth={1.8} />}
        </span>
        <div className="min-w-0 flex-1">
          <p className="lf-h4">{toast.title}</p>
          <p className="mt-px lf-body-sm">{toast.message}</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="닫기"
          className="shrink-0 rounded-r-1 p-px opacity-60 transition-opacity duration-base ease-standard hover:opacity-100"
        >
          <XIcon size={16} strokeWidth={1.8} />
        </button>
      </div>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* 확인서 발송 — 그 기회의 확인서 목록(SF 값 그대로) + 선택 → 초안 → 일괄 발송  */
/* -------------------------------------------------------------------------- */

function ConfirmationsTab({ opp, onBack }: { opp: OpportunityRow; onBack: () => void }) {
  const [rows, setRows] = useState<QuoteRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [composing, setComposing] = useState<QuoteRow[] | null>(null)
  // 기회 단위라 전체 상태(초안 포함) 기본 노출 — 방금 발행한 초안 확인서도 바로 보이게.
  const [showAll, setShowAll] = useState(true)
  const [genFor, setGenFor] = useState<QuoteRow | null>(null) // PDF 생성 미리보기 대상
  const [lastTemplate, setLastTemplate] = useState<string | null>(null) // 직전 선택 템플릿
  const [bulkPdfFor, setBulkPdfFor] = useState<QuoteRow[] | null>(null) // PDF 일괄 생성 대상
  const [sendFor, setSendFor] = useState<QuoteRow[] | null>(null) // 일괄 발송 확인 대상
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null)
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    let live = true
    setLoading(true)
    setError(null)
    setSelected(new Set())
    getOpportunityQuotes(opp.oppId, showAll ? '전체' : undefined)
      .then((r) => live && setRows(r.rows))
      .catch((e: unknown) => {
        if (live)
          setError(
            e instanceof ConfirmationError ? humanizeConfirmationError(e.message) : '확인서 조회 실패',
          )
      })
      .finally(() => live && setLoading(false))
    return () => {
      live = false
    }
  }, [opp.oppId, showAll, reloadKey])

  // 선택 가능 = 아직 발송 전(포털 발송 이력 / SF 전송완료 둘 다 아님). 동작별 대상은 아래에서 거른다.
  const selectable = (r: QuoteRow) =>
    r.status !== '전송완료' && r.mail?.status !== 'sent'
  // 초안 대상 = 이메일 있음. PDF 가 없으면 초안 생성 때 고른 템플릿으로 자동 생성·SF 저장.
  const draftReady = (r: QuoteRow) => Boolean(r.email)
  // 발송 가능 = 저장된 초안이 있음(초안 또는 직전 발송 실패). 누가 저장했든 발송 가능.
  const sendReady = (r: QuoteRow) => Boolean(r.mail && (r.mail.status === 'drafted' || r.mail.status === 'failed'))
  const mailStatus = useMailStatus()
  const mailReady = Boolean(mailStatus?.configured)

  const selectableIds = rows.filter(selectable).map((r) => r.id)
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id))
  const someSelected = selected.size > 0 && !allSelected
  const picked = rows.filter((r) => selected.has(r.id))
  const toPdf = picked.filter((r) => !r.hasPdf)
  const toDraft = picked.filter(draftReady)
  const toSend = picked.filter(sendReady)

  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(selectableIds))
  const toggleOne = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  if (composing) {
    return (
      <EmailComposeView
        opp={opp}
        quotes={composing}
        onBack={(changed, n) => {
          setComposing(null)
          setNotice(n ?? null)
          if (changed) setReloadKey((k) => k + 1)
        }}
      />
    )
  }

  return (
    <div className="mt-s-5">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-s-2 rounded-r-1 border border-line-hairline bg-surface-base px-s-3 py-s-2 lf-body-sm text-fg-2 transition-colors duration-base ease-standard hover:border-line-soft hover:bg-surface-soft hover:text-fg-1 focus-visible:outline-none focus-visible:shadow-focus"
      >
        <ChevronLeft size={14} strokeWidth={1.5} />
        판매상품
      </button>

      <div className="mt-s-4 flex flex-wrap items-center justify-between gap-s-3 border border-line-hairline bg-surface-soft px-s-4 py-s-3">
        <div className="flex flex-wrap items-center gap-s-3">
          <p className="lf-body-sm text-fg-2">
            {loading ? (
              '불러오는 중…'
            ) : (
              <>
                확인서 <span className="lf-num text-fg-1">{rows.length.toLocaleString()}</span>건
                {selected.size ? (
                  <span className="text-fg-1"> · {selected.size.toLocaleString()}건 선택</span>
                ) : null}
              </>
            )}
          </p>
          <span className="inline-flex items-center rounded-full border border-line-hairline bg-surface-base px-s-3 py-px lf-caption text-fg-2">
            발신 <span className="lf-num text-fg-1">&nbsp;{LECOMMEND_FROM}</span>
          </span>
          <button
            type="button"
            onClick={() => setShowAll((v) => !v)}
            className="rounded-full border border-line-hairline bg-surface-base px-s-3 py-px lf-caption text-fg-2 transition-colors duration-base ease-standard hover:border-line-soft hover:text-fg-1"
          >
            {showAll ? '발송 대상만' : '전체 보기'}
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-s-3">
          <button
            type="button"
            disabled={toPdf.length === 0}
            title={
              picked.length && !toPdf.length ? '선택한 확인서는 모두 PDF가 저장돼 있어요.' : undefined
            }
            onClick={() => setBulkPdfFor(toPdf)}
            className="inline-flex items-center gap-s-2 rounded-r-2 border border-line-soft bg-surface-base px-s-4 py-s-3 lf-h4 text-fg-1 transition-colors duration-base ease-standard hover:border-line-strong hover:bg-surface-soft disabled:cursor-not-allowed disabled:opacity-50"
          >
            <FileCheck2 size={15} strokeWidth={1.6} />
            PDF 일괄 생성{toPdf.length ? ` (${toPdf.length})` : ''}
          </button>
          <button
            type="button"
            disabled={toDraft.length === 0}
            title={
              picked.length && !toDraft.length ? '이메일이 등록된 확인서만 초안을 만들 수 있어요.' : undefined
            }
            onClick={() => setComposing(toDraft)}
            className="inline-flex items-center gap-s-2 rounded-r-2 border border-line-soft bg-surface-base px-s-4 py-s-3 lf-h4 text-fg-1 transition-colors duration-base ease-standard hover:border-line-strong hover:bg-surface-soft disabled:cursor-not-allowed disabled:opacity-50"
          >
            <FileText size={15} strokeWidth={1.6} />
            초안 작성{toDraft.length ? ` (${toDraft.length})` : ''}
          </button>
          <button
            type="button"
            disabled={toSend.length === 0 || !mailReady}
            title={
              !mailReady
                ? '메일 발송(SMTP)이 서버에 아직 설정되지 않았어요.'
                : picked.length && !toSend.length
                  ? '선택한 확인서에 저장된 초안이 없어요. 초안을 먼저 작성하세요.'
                  : undefined
            }
            onClick={() => setSendFor(toSend)}
            className="inline-flex items-center gap-s-2 rounded-r-2 bg-brand-pink px-s-5 py-s-3 lf-h4 text-brand-brown transition-colors duration-base ease-standard hover:bg-pink-500 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Send size={15} strokeWidth={1.6} />
            일괄 발송{toSend.length ? ` (${toSend.length})` : ''}
          </button>
        </div>
      </div>

      {notice ? <ResultBanner ok={notice.ok} text={notice.text} /> : null}
      {error ? <p className="mt-s-4 lf-body-sm text-status-danger">▪ {error}</p> : null}

      {!error ? (
        <div className="mt-s-4 overflow-x-auto border border-line-hairline">
          <table className="w-full border-separate border-spacing-0">
            <thead>
              <tr>
                <th className="sticky left-0 z-[1] w-10 border-b border-line-soft bg-surface-soft px-s-3 py-s-2 text-left">
                  <input
                    type="checkbox"
                    aria-label="전체 선택"
                    checked={allSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = someSelected
                    }}
                    disabled={selectableIds.length === 0}
                    onChange={toggleAll}
                    className="h-4 w-4 cursor-pointer accent-brand-pink disabled:cursor-not-allowed"
                  />
                </th>
                <Th>크리에이터</Th>
                <Th>판매상품</Th>
                <Th>브랜드 제품명</Th>
                <Th align="right">판매단가</Th>
                <Th align="right">운영관리비</Th>
                <Th align="right">콘텐츠제작단가</Th>
                <Th align="right">매출액</Th>
                <Th>후입금 정산</Th>
                <Th>작성자</Th>
                <Th>작성일시</Th>
                <Th>상태</Th>
                <Th>PDF</Th>
                <Th>메일</Th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <Td colSpan={14} className="text-center text-fg-3">
                    불러오는 중…
                  </Td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <Td colSpan={14} className="text-center text-fg-3">
                    이 기회에 확인서가 없어요.
                  </Td>
                </tr>
              ) : (
                rows.map((r) => {
                  const can = selectable(r)
                  const ls = r.lines.length ? r.lines : [null]
                  const col = (f: (l: QuoteLine) => React.ReactNode) => (
                    <span className="flex flex-col gap-px">
                      {ls.map((l, i) => (
                        <span key={i} className="whitespace-nowrap">
                          {l ? (f(l) ?? '—') : '—'}
                        </span>
                      ))}
                    </span>
                  )
                  return (
                    <tr key={r.id} className={selected.has(r.id) ? 'bg-pink-50/40' : undefined}>
                      <td className="sticky left-0 z-[1] border-b border-line-hairline bg-surface-base px-s-3 py-s-3 align-top">
                        <input
                          type="checkbox"
                          aria-label={`${r.creator ?? r.name} 선택`}
                          checked={selected.has(r.id)}
                          disabled={!can}
                          onChange={() => toggleOne(r.id)}
                          className="h-4 w-4 cursor-pointer accent-brand-pink disabled:cursor-not-allowed disabled:opacity-40"
                        />
                      </td>
                      <Td className="text-fg-1">
                        <span className="block whitespace-nowrap">{r.creator || '—'}</span>
                        {r.email ? (
                          <span className="block lf-caption text-fg-3">{r.email}</span>
                        ) : (
                          <span className="block lf-caption text-status-warning">이메일 미등록</span>
                        )}
                        {r.quoteNumber ? (
                          <span className="lf-num lf-caption text-fg-3">{r.quoteNumber}</span>
                        ) : null}
                      </Td>
                      <Td className="text-fg-1">{col((l) => l.product)}</Td>
                      <Td className="text-fg-2">{col((l) => l.brandItem)}</Td>
                      <Td align="right" className="lf-num">{col((l) => wonN(l.unitPrice))}</Td>
                      <Td align="right" className="lf-num">{col((l) => wonN(l.pricePrj))}</Td>
                      <Td align="right" className="lf-num">{col((l) => wonN(l.contentsFee))}</Td>
                      <Td align="right" className="lf-num text-fg-1">{col((l) => wonN(l.totalPrice))}</Td>
                      <Td className="lf-num text-fg-2">{col((l) => l.deferredDate)}</Td>
                      <Td className="whitespace-nowrap text-fg-2">{r.createdBy || '—'}</Td>
                      <Td className="lf-num whitespace-nowrap text-fg-2">{fmtDateTime(r.createdDate)}</Td>
                      <Td>
                        <span
                          className={`inline-flex whitespace-nowrap rounded-r-1 border px-s-2 py-px lf-caption ${
                            r.status === '전송완료'
                              ? 'border-brand-blue text-brand-blue'
                              : 'border-line-soft text-fg-2'
                          }`}
                        >
                          {r.status || '—'}
                        </span>
                      </Td>
                      <Td>
                        {r.hasPdf ? (
                          <button
                            type="button"
                            onClick={() => window.open(quotePdfUrl(r.id), '_blank', 'noopener')}
                            className="inline-flex items-center gap-s-1 whitespace-nowrap rounded-r-1 border border-line-hairline bg-surface-base px-s-2 py-px lf-caption text-fg-2 transition-colors duration-base ease-standard hover:border-line-soft hover:text-fg-1"
                          >
                            <Eye size={12} strokeWidth={1.6} />
                            미리보기
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setGenFor(r)}
                            className="inline-flex items-center gap-s-1 whitespace-nowrap rounded-r-1 border border-brand-pink/50 bg-pink-50 px-s-2 py-px lf-caption text-fg-1 transition-colors duration-base ease-standard hover:border-brand-pink"
                          >
                            <FileText size={12} strokeWidth={1.6} />
                            PDF 생성
                          </button>
                        )}
                      </Td>
                      <Td>
                        <MailStatusChip mail={r.mail} />
                      </Td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      ) : null}

      <p className="mt-s-4 flex items-start gap-s-2 lf-caption text-fg-3">
        <span aria-hidden>▪</span>
        <span>
          순서: 체크 → <span className="text-fg-2">PDF 일괄 생성</span>(PDF 없는 건, 템플릿 선택) →{' '}
          <span className="text-fg-2">초안 작성</span>(이메일 있는 건, PDF가 없으면 이때 자동 생성) →{' '}
          <span className="text-fg-2">일괄 발송</span>(초안이 저장된 건). 발송은{' '}
          <span className="text-fg-2">{LECOMMEND_FROM}</span>에서 크리에이터별 PDF를 첨부해 나가요.
        </span>
      </p>

      {genFor ? (
        <GeneratePdfModal
          quote={genFor}
          initialTemplate={lastTemplate}
          onClose={() => setGenFor(null)}
          onSaved={(t) => {
            setGenFor(null)
            setLastTemplate(t)
            setReloadKey((k) => k + 1)
          }}
        />
      ) : null}

      {bulkPdfFor ? (
        <BulkPdfModal
          quotes={bulkPdfFor}
          initialTemplate={lastTemplate}
          onClose={(r) => {
            setBulkPdfFor(null)
            if (r) {
              setLastTemplate(r.template)
              setNotice({
                ok: r.failed === 0,
                text:
                  `PDF ${r.saved}건 생성·저장` +
                  (r.failed ? `, ${r.failed}건 실패` : '') +
                  ` (${r.template})`,
              })
              setReloadKey((k) => k + 1)
            }
          }}
        />
      ) : null}

      {sendFor ? (
        <SendConfirmModal
          quotes={sendFor}
          onClose={() => setSendFor(null)}
          onDone={(r) => {
            setSendFor(null)
            setNotice(r)
            setReloadKey((k) => k + 1)
          }}
        />
      ) : null}
    </div>
  )
}

/** 포털 메일 이력 칩 — 없음 / 초안 / 발송완료 / 실패. */
function MailStatusChip({ mail }: { mail: QuoteRow['mail'] }) {
  if (!mail) return <span className="lf-caption text-fg-3">—</span>
  if (mail.status === 'sent') {
    return (
      <span className="inline-flex flex-col">
        <span className="inline-flex w-fit whitespace-nowrap rounded-r-1 border border-brand-blue px-s-2 py-px lf-caption text-brand-blue">
          발송완료
        </span>
        <span className="mt-px lf-num lf-caption whitespace-nowrap text-fg-3">{fmtDateTime(mail.sentAt)}</span>
      </span>
    )
  }
  if (mail.status === 'sending') {
    return (
      <span className="inline-flex whitespace-nowrap rounded-r-1 border border-line-soft px-s-2 py-px lf-caption text-fg-2">
        발송 중
      </span>
    )
  }
  if (mail.status === 'failed') {
    return (
      <span
        title={mail.error ?? undefined}
        className="inline-flex whitespace-nowrap rounded-r-1 border border-status-danger px-s-2 py-px lf-caption text-status-danger"
      >
        발송 실패
      </span>
    )
  }
  return (
    <span className="inline-flex flex-col">
      <span className="inline-flex w-fit whitespace-nowrap rounded-r-1 border border-line-soft px-s-2 py-px lf-caption text-fg-1">
        초안
      </span>
    </span>
  )
}

function ResultBanner({ ok, text }: { ok: boolean; text: string }) {
  return (
    <div
      className={`mt-s-4 border-l-2 bg-surface-soft px-s-4 py-s-3 lf-body-sm ${
        ok ? 'border-l-brand-blue text-fg-1' : 'border-l-status-danger text-status-danger'
      }`}
    >
      ▪ {text}
    </div>
  )
}

/** 메일 발송(SMTP) 설정 상태 — 로딩 중 null, 실패 시 미설정으로 본다. */
function useMailStatus(): MailStatus | null {
  const [st, setSt] = useState<MailStatus | null>(null)
  useEffect(() => {
    let live = true
    getMailStatus()
      .then((s) => live && setSt(s))
      .catch(() => live && setSt({ configured: false, from: LECOMMEND_FROM, fromName: '레코멘드' }))
    return () => {
      live = false
    }
  }, [])
  return st
}

/** 'xxx.pdf' → 'xxx' (입력칸은 확장자 없이 보여주고 '.pdf' 는 옆에 고정 표시). */
function stripPdf(name: string | null | undefined): string {
  return String(name ?? '').replace(/\.pdf$/i, '')
}

/** 파일명용 크리에이터명 — 서버 기본 파일명 규칙과 같은 정리(금지 문자 → '-', 공백 정리). */
function cleanFileNamePart(s: string | null | undefined): string {
  return String(s ?? '')
    .replace(/[\\/:*?"<>|\r\n\t]+/g, '-')
    .replace(/\s+/g, ' ')
    .trim()
}
/** 'Leferi_확인서_{크리에이터명}' — 파일명에서 고정되는 앞부분. */
function fileNamePrefix(creator: string | null | undefined): string {
  return ['Leferi_확인서', cleanFileNamePart(creator)].filter(Boolean).join('_')
}
/** 파일명(확장자 제외)에서 'Leferi_확인서_{크리에이터명}_' 뒤 부분. 형식이 다르면 전체. */
function fileNameSuffix(fileBase: string, creator: string | null | undefined): string {
  const pre = `${fileNamePrefix(creator)}_`
  return fileBase.startsWith(pre) ? fileBase.slice(pre.length) : fileBase
}

/** 첨부 PDF 파일명 입력 — 확장자(.pdf)는 고정, 기본값으로 되돌리기. */
function FileNameInput({
  value,
  defaultValue,
  disabled,
  label,
  onChange,
}: {
  value: string
  defaultValue: string
  disabled?: boolean
  label: string
  onChange: (v: string) => void
}) {
  const changed = value.trim() !== defaultValue
  return (
    <span className="flex min-w-0 flex-col gap-px">
      <span className="flex min-w-0 items-center gap-s-1">
        <input
          type="text"
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          placeholder={defaultValue}
          aria-label={label}
          className="min-w-[200px] flex-1 rounded-r-1 border border-line-soft bg-surface-base px-s-2 py-s-1 lf-caption text-fg-1 placeholder:text-fg-3 focus-visible:border-brand-blue focus-visible:outline-none focus-visible:shadow-focus disabled:cursor-not-allowed disabled:bg-surface-soft disabled:text-fg-2"
        />
        <span className="lf-caption text-fg-3">.pdf</span>
      </span>
      {changed && !disabled ? (
        <button
          type="button"
          onClick={() => onChange(defaultValue)}
          className="w-fit lf-caption text-fg-3 underline-offset-2 hover:text-fg-1 hover:underline"
        >
          기본 파일명으로
        </button>
      ) : null}
    </span>
  )
}

/** 템플릿 이름 → PDF 배너 부제와 같은 표기("[확인서]" 태그 제거). */
function templateSubtitleC(name: string | null): string {
  return (name ?? '').replace(/^\[확인서\]\s*/, '').trim()
}

/** ISO → "2026-09-23 11:31" (로컬). */
function fmtDateTime(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`
}

/**
 * 확인서 PDF 재현 생성 — 템플릿 선택 → 미리보기(iframe) → Salesforce 저장.
 * 템플릿은 레이아웃이 같고 배너의 "Business Certificate" 아래 부제만 템플릿 이름으로 바뀐다.
 */
function GeneratePdfModal({
  quote,
  initialTemplate,
  onClose,
  onSaved,
}: {
  quote: QuoteRow
  /** 직전에 고른 템플릿(같은 기회에서 연속 생성 시 기본값). */
  initialTemplate: string | null
  onClose: () => void
  onSaved: (template: string) => void
}) {
  const [template, setTemplate] = useState<string | null>(initialTemplate)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const templates = usePdfTemplates(setErr)

  const save = async () => {
    if (!template) return
    setSaving(true)
    setErr(null)
    try {
      await saveGeneratedPdf(quote.id, template)
      onSaved(template)
    } catch (e) {
      setErr(e instanceof ConfirmationError ? humanizeConfirmationError(e.message) : '저장 실패')
      setSaving(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-brand-brown/40 p-s-5"
      role="dialog"
      aria-modal="true"
      aria-labelledby="gen-pdf-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !saving) onClose()
      }}
    >
      <div className="mt-s-6 flex h-[88vh] w-full max-w-[1120px] flex-col border border-line-hairline bg-surface-base shadow-pop">
        <div className="flex items-center justify-between border-b border-line-hairline px-s-5 py-s-4">
          <div>
            <p className="lf-eyebrow text-fg-3">▪ 확인서 PDF 생성</p>
            <h3 id="gen-pdf-title" className="mt-s-1 lf-h3 text-fg-1">
              {quote.creator || quote.name}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="닫기"
            className="inline-flex items-center justify-center rounded-r-1 border border-line-hairline p-s-2 text-fg-2 transition-colors duration-base ease-standard hover:border-line-soft hover:text-fg-1"
          >
            <XIcon size={16} strokeWidth={1.6} />
          </button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col md:flex-row">
          <TemplatePicker
            templates={templates}
            loading={templates === null && !err}
            value={template}
            onChange={setTemplate}
          />

          {template ? (
            <iframe
              key={template}
              title="확인서 PDF 미리보기"
              src={generatePdfUrl(quote.id, template)}
              className="min-h-0 flex-1 border-0 bg-surface-soft"
            />
          ) : (
            <div className="flex min-h-0 flex-1 items-center justify-center bg-surface-soft px-s-5">
              <p className="text-center lf-body-sm text-fg-3">
                왼쪽에서 템플릿을 고르면 미리보기가 떠요.
              </p>
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-s-3 border-t border-line-hairline px-s-5 py-s-4">
          <p className="min-w-0 flex-1 lf-caption text-fg-3">
            ▪ 저장하면 Salesforce 확인서에 첨부되고, 그 파일이 메일에 첨부돼요.
            {err ? <span className="text-status-danger"> · {err}</span> : null}
          </p>
          <div className="flex items-center gap-s-3">
            <button
              type="button"
              disabled={saving}
              onClick={onClose}
              className="rounded-r-1 border border-line-hairline bg-surface-base px-s-4 py-s-2 lf-body-sm text-fg-2 transition-colors duration-base ease-standard hover:border-line-soft hover:text-fg-1 disabled:opacity-50"
            >
              닫기
            </button>
            <button
              type="button"
              disabled={saving || !template}
              title={!template ? '템플릿을 먼저 선택해 주세요.' : undefined}
              onClick={save}
              className="inline-flex items-center gap-s-2 rounded-r-2 bg-brand-pink px-s-5 py-s-2 lf-h4 text-brand-brown transition-colors duration-base ease-standard hover:bg-pink-500 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving ? '저장 중…' : 'Salesforce에 저장'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

/** 확인서 템플릿 목록(한 번 로드). 실패 시 onError. */
function usePdfTemplates(onError: (msg: string) => void): string[] | null {
  const [templates, setTemplates] = useState<string[] | null>(null)
  useEffect(() => {
    let live = true
    getPdfTemplates()
      .then((r) => live && setTemplates(r.rows))
      .catch(() => live && onError('템플릿 목록을 불러오지 못했어요.'))
    return () => {
      live = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  return templates
}

/** 템플릿 라디오 목록 — PDF 생성(단건/일괄) 공용. */
function TemplatePicker({
  templates,
  loading,
  value,
  disabled,
  onChange,
}: {
  templates: string[] | null
  loading: boolean
  value: string | null
  disabled?: boolean
  onChange: (t: string) => void
}) {
  return (
    <fieldset
      disabled={disabled}
      className="max-h-[38%] shrink-0 overflow-y-auto border-b border-line-hairline md:max-h-none md:w-[300px] md:border-b-0 md:border-r"
    >
      <legend className="sr-only">확인서 템플릿</legend>
      <p className="px-s-4 pb-s-2 pt-s-4 lf-eyebrow text-fg-3">▪ 템플릿 선택</p>
      {loading ? <p className="px-s-4 py-s-3 lf-caption text-fg-3">불러오는 중…</p> : null}
      <ul className="pb-s-3">
        {(templates ?? []).map((t) => {
          const on = value === t
          return (
            <li key={t}>
              <label
                className={`flex cursor-pointer items-start gap-s-3 border-l-2 px-s-4 py-s-2 lf-body-sm transition-colors duration-base ease-standard ${
                  on
                    ? 'border-l-brand-pink bg-pink-50 text-fg-1'
                    : 'border-l-transparent text-fg-2 hover:bg-surface-soft hover:text-fg-1'
                }`}
              >
                <input
                  type="radio"
                  name="pdf-template"
                  value={t}
                  checked={on}
                  onChange={() => onChange(t)}
                  className="mt-[3px] h-4 w-4 shrink-0 accent-brand-pink"
                />
                <span>{t}</span>
              </label>
            </li>
          )
        })}
      </ul>
    </fieldset>
  )
}

type BulkState = 'wait' | 'run' | 'done' | 'fail'

/**
 * PDF 일괄 생성 — 템플릿 1개 선택 → 첫 건 미리보기 → 선택한 확인서마다 순서대로
 * 생성 + Salesforce(QuoteDocument) 저장. ⚠️ SF 쓰기. 실패 건은 건너뛰고 계속.
 */
function BulkPdfModal({
  quotes,
  initialTemplate,
  onClose,
}: {
  quotes: QuoteRow[]
  initialTemplate: string | null
  /** 실행했으면 결과, 그냥 닫았으면 null. */
  onClose: (r: { template: string; saved: number; failed: number } | null) => void
}) {
  const [template, setTemplate] = useState<string | null>(initialTemplate)
  const [err, setErr] = useState<string | null>(null)
  const templates = usePdfTemplates(setErr)
  const [states, setStates] = useState<Map<string, { s: BulkState; error?: string }>>(new Map())
  const [running, setRunning] = useState(false)
  const [finished, setFinished] = useState(false)
  const [previewId, setPreviewId] = useState(quotes[0]?.id ?? '')

  const saved = [...states.values()].filter((x) => x.s === 'done').length
  const failed = [...states.values()].filter((x) => x.s === 'fail').length
  const done = saved + failed

  const run = async () => {
    if (!template) return
    setRunning(true)
    setErr(null)
    for (const q of quotes) {
      if (states.get(q.id)?.s === 'done') continue // 재시도 시 성공분은 건너뜀
      setStates((m) => new Map(m).set(q.id, { s: 'run' }))
      try {
        await saveGeneratedPdf(q.id, template)
        setStates((m) => new Map(m).set(q.id, { s: 'done' }))
      } catch (e) {
        const msg = e instanceof ConfirmationError ? humanizeConfirmationError(e.message) : '저장 실패'
        setStates((m) => new Map(m).set(q.id, { s: 'fail', error: msg }))
      }
    }
    setRunning(false)
    setFinished(true)
  }

  const close = () => {
    if (running) return
    onClose(finished && template ? { template, saved, failed } : null)
  }

  const label = (st?: { s: BulkState; error?: string }) => {
    if (!st || st.s === 'wait') return <span className="lf-caption text-fg-3">대기</span>
    if (st.s === 'run') return <span className="lf-caption text-fg-1">생성 중…</span>
    if (st.s === 'done') return <span className="lf-caption text-brand-blue">저장됨</span>
    return (
      <span className="lf-caption text-status-danger" title={st.error}>
        실패
      </span>
    )
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-brand-brown/40 p-s-5"
      role="dialog"
      aria-modal="true"
      aria-labelledby="bulk-pdf-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) close()
      }}
    >
      <div className="mt-s-6 flex h-[88vh] w-full max-w-[1200px] flex-col border border-line-hairline bg-surface-base shadow-pop">
        <div className="flex items-center justify-between border-b border-line-hairline px-s-5 py-s-4">
          <div>
            <p className="lf-eyebrow text-fg-3">▪ PDF 일괄 생성</p>
            <h3 id="bulk-pdf-title" className="mt-s-1 lf-h3 text-fg-1">
              확인서 {quotes.length}건
            </h3>
          </div>
          <button
            type="button"
            onClick={close}
            disabled={running}
            aria-label="닫기"
            className="inline-flex items-center justify-center rounded-r-1 border border-line-hairline p-s-2 text-fg-2 transition-colors duration-base ease-standard hover:border-line-soft hover:text-fg-1 disabled:opacity-50"
          >
            <XIcon size={16} strokeWidth={1.6} />
          </button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col md:flex-row">
          <TemplatePicker
            templates={templates}
            loading={templates === null && !err}
            value={template}
            disabled={running || finished}
            onChange={setTemplate}
          />

          <div className="flex min-h-0 flex-1 flex-col md:flex-row">
            <ul className="max-h-[30%] shrink-0 overflow-y-auto border-b border-line-hairline md:max-h-none md:w-[260px] md:border-b-0 md:border-r">
              <li className="px-s-4 pb-s-2 pt-s-4 lf-eyebrow text-fg-3">▪ 대상 · 미리보기</li>
              {quotes.map((q) => {
                const on = previewId === q.id
                return (
                  <li key={q.id}>
                    <button
                      type="button"
                      onClick={() => setPreviewId(q.id)}
                      className={`flex w-full items-center justify-between gap-s-3 border-l-2 px-s-4 py-s-2 text-left lf-body-sm transition-colors duration-base ease-standard ${
                        on
                          ? 'border-l-brand-blue bg-surface-soft text-fg-1'
                          : 'border-l-transparent text-fg-2 hover:bg-surface-soft hover:text-fg-1'
                      }`}
                    >
                      <span className="min-w-0 truncate">{q.creator || q.name}</span>
                      <span className="shrink-0">{label(states.get(q.id))}</span>
                    </button>
                  </li>
                )
              })}
            </ul>

            {template && previewId ? (
              <iframe
                key={`${template}:${previewId}`}
                title="확인서 PDF 미리보기"
                src={generatePdfUrl(previewId, template)}
                className="min-h-0 flex-1 border-0 bg-surface-soft"
              />
            ) : (
              <div className="flex min-h-0 flex-1 items-center justify-center bg-surface-soft px-s-5">
                <p className="text-center lf-body-sm text-fg-3">템플릿을 고르면 미리보기가 떠요.</p>
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-s-3 border-t border-line-hairline px-s-5 py-s-4">
          <div className="min-w-0 flex-1">
            {running || finished ? (
              <>
                <div
                  className="h-[4px] w-full max-w-[360px] bg-surface-soft"
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={quotes.length}
                  aria-valuenow={done}
                >
                  <div
                    className="h-full bg-brand-pink transition-[width] duration-base ease-standard"
                    style={{ width: `${quotes.length ? (done / quotes.length) * 100 : 0}%` }}
                  />
                </div>
                <p className="mt-s-2 lf-caption text-fg-2">
                  <span className="lf-num">
                    {done}/{quotes.length}
                  </span>{' '}
                  처리 · 저장 {saved}
                  {failed ? <span className="text-status-danger"> · 실패 {failed}</span> : null}
                </p>
              </>
            ) : (
              <p className="lf-caption text-fg-3">
                ▪ 선택한 템플릿으로 {quotes.length}건을 만들어 각 Salesforce 확인서에 저장해요.
              </p>
            )}
            {err ? <p className="mt-s-1 lf-caption text-status-danger">▪ {err}</p> : null}
          </div>
          <div className="flex items-center gap-s-3">
            <button
              type="button"
              disabled={running}
              onClick={close}
              className="rounded-r-1 border border-line-hairline bg-surface-base px-s-4 py-s-2 lf-body-sm text-fg-2 transition-colors duration-base ease-standard hover:border-line-soft hover:text-fg-1 disabled:opacity-50"
            >
              {finished ? '완료' : '닫기'}
            </button>
            {!finished || failed ? (
              <button
                type="button"
                disabled={running || !template}
                title={!template ? '템플릿을 먼저 선택해 주세요.' : undefined}
                onClick={run}
                className="inline-flex items-center gap-s-2 rounded-r-2 bg-brand-pink px-s-5 py-s-2 lf-h4 text-brand-brown transition-colors duration-base ease-standard hover:bg-pink-500 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {running
                  ? '생성 중…'
                  : finished
                    ? `실패 ${failed}건 다시 시도`
                    : `${quotes.length}건 생성·저장`}
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* 이메일 초안 — 공통 제목·본문(편집) + 크리에이터별 수신자·SF PDF 첨부         */
/* 발신은 cr_lecommend@leferi.com 고정(disabled).                               */
/* -------------------------------------------------------------------------- */

interface EmailDraft {
  quoteId: string
  creator: string
  email: string
  /** 첨부 표시명(크리에이터가 받을 파일명). PDF 가 아직 없으면 null. */
  pdfName: string | null
  /** 메일 첨부 파일명 — PDF 를 올리거나 생성한 뒤 표시에 쓴다. */
  mailFileName: string
  /** 첨부 PDF 파일명(확장자 제외) — 수정 가능. */
  fileBase: string
}

/**
 * 기회 이름 → 메일 템플릿 변수. "{계정}_{브랜드}_{프로그램}_{YY년 M월}" 패턴.
 *   (주)에뛰드_에뛰드_뷰티즌 11기_26년 9월 → month "9월", campaign "에뛰드 뷰티즌 11기", program "뷰티즌 11기"
 */
function deriveCampaign(oppName: string): { month: string; campaign: string; program: string } {
  const parts = oppName.split('_').map((s) => s.trim())
  const m = oppName.match(/(\d{1,2})월/)
  const month = m ? `${Number(m[1])}월` : ''
  const rest = parts.slice(1).filter((p) => !/\d{2}년\s*\d{1,2}월/.test(p))
  const brand = rest[0] ?? parts[0] ?? ''
  const program = rest[1] ?? ''
  return { month, campaign: [brand, program].filter(Boolean).join(' '), program: program || brand }
}

/** 기본 템플릿 — 레코멘드 확인서 메일(예시: 9월 클리오 숏비디오리그) 형식. */
function defaultTemplate(opp: OpportunityRow): { subject: string; body: string } {
  const { month, campaign, program } = deriveCampaign(opp.oppName)
  const head = [month, campaign].filter(Boolean).join(' ')
  return {
    subject: `[Lecommend] ${head} 비즈니스 확인서 전달드립니다`,
    body: [
      '안녕하세요 크리에이터님,',
      '레코멘드 입니다.',
      '',
      `${head}건으로`,
      '비즈니스 확인서 전달드리고자 메일드립니다.',
      '',
      '메일 내 첨부드린 파일 확인 부탁드리며,',
      '확인서 내 정산 정보 수정이 필요하신 경우',
      '[레코멘드 카카오톡 플친]으로 문의주시면 빠르게 안내 도와드리겠습니다.',
      '',
      `항상 저희 ${program}에 애정갖고 참여해 주셔서 감사드립니다.`,
      '',
      '감사합니다.',
      '레코멘드 고객관리팀',
    ].join('\n'),
  }
}

function EmailComposeView({
  opp,
  quotes,
  onBack,
}: {
  opp: OpportunityRow
  quotes: QuoteRow[]
  /** changed = 초안/발송이 일어나 목록 재조회가 필요한지. notice = 목록에 띄울 결과. */
  onBack: (changed: boolean, notice?: { ok: boolean; text: string }) => void
}) {
  const tpl = defaultTemplate(opp)
  const [subject, setSubject] = useState(tpl.subject)
  const [body, setBody] = useState(tpl.body)
  const [drafts, setDrafts] = useState<EmailDraft[]>(() =>
    // 첨부 표시는 크리에이터가 받을 파일명(SF 저장명과 별개). PDF 가 없으면 null.
    quotes.map((r) => ({
      quoteId: r.id,
      creator: r.creator ?? '',
      email: r.email ?? '',
      pdfName: r.hasPdf ? r.mailFileName : null,
      mailFileName: r.mailFileName,
      fileBase: stripPdf(r.mailFileName),
    })),
  )
  const mailStatus = useMailStatus()
  const mailReady = Boolean(mailStatus?.configured)
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null)
  // 초안 생성 결과(quoteId → 성공/실패 사유). 성공분만 바로 발송 가능.
  const [draftRes, setDraftRes] = useState<Map<string, { ok: boolean; error?: string }>>(new Map())
  const [changed, setChanged] = useState(false)
  const [sendFor, setSendFor] = useState<QuoteRow[] | null>(null)
  // PDF 가 없는 확인서용 템플릿 — 초안 생성 때 이 템플릿으로 생성·SF 저장 후 첨부.
  const [template, setTemplate] = useState<string | null>(null)
  const [tplErr, setTplErr] = useState<string | null>(null)
  const templates = usePdfTemplates(setTplErr)
  // 초안 생성 시점의 내용 — 이후 수정하면 발송 전 초안을 다시 만들어야 한다.
  const [draftedKey, setDraftedKey] = useState<string | null>(null)

  const update = (i: number, patch: Partial<EmailDraft>) =>
    setDrafts((prev) => prev.map((d, idx) => (idx === i ? { ...d, ...patch } : d)))

  const contentKey = JSON.stringify([subject, body, drafts.map((d) => [d.email.trim(), d.fileBase.trim()])])
  const dirty = draftedKey !== null && draftedKey !== contentKey

  const saveDrafts = async () => {
    const key = contentKey
    setBusy(true)
    setResult(null)
    try {
      const r = await createDrafts(
        opp.oppId,
        drafts.map((d) => ({ quoteId: d.quoteId, to: d.email.trim(), subject, body, fileName: d.fileBase.trim() })),
        needsPdf.length ? template : null,
      )
      setDraftRes(
        new Map(r.results.map((x) => [x.quoteId, { ok: x.status === 'created', error: x.error }])),
      )
      // 첨부 표시를 SF 실제 파일명으로 갱신(이번에 생성·저장된 PDF 포함).
      const names = new Map(r.results.filter((x) => x.attachmentName).map((x) => [x.quoteId, x.attachmentName!]))
      if (names.size) {
        setDrafts((prev) => prev.map((d) => (names.has(d.quoteId) ? { ...d, pdfName: names.get(d.quoteId)! } : d)))
      }
      setChanged(true)
      setDraftedKey(key)
      setResult({
        ok: r.summary.failed === 0,
        text:
          `초안 ${r.summary.created}건 저장` +
          (r.summary.failed ? `, ${r.summary.failed}건 실패` : '') +
          '. 내용을 확인한 뒤 일괄 발송하세요.',
      })
    } catch (e) {
      setResult({
        ok: false,
        text: e instanceof ConfirmationError ? humanizeConfirmationError(e.message) : '초안 저장 실패',
      })
    } finally {
      setBusy(false)
    }
  }

  // 초안이 만들어진 확인서만 발송 대상(수신자는 초안에 기록된 값).
  const drafted = drafts.filter((d) => draftRes.get(d.quoteId)?.ok)
  const sendTargets: QuoteRow[] = quotes
    .filter((q) => draftRes.get(q.id)?.ok)
    .map((q) => ({
      ...q,
      email: drafts.find((d) => d.quoteId === q.id)?.email.trim() ?? q.email,
      mail: null,
    }))
  const needsPdf = drafts.filter((d) => !d.pdfName)
  const blocked =
    drafts.some((d) => !d.email.trim()) ||
    !subject.trim() ||
    !body.trim() ||
    (needsPdf.length > 0 && !template)

  const inputCls =
    'mt-s-2 w-full rounded-r-1 border border-line-soft bg-surface-base px-s-3 py-s-2 lf-body-sm text-fg-1 focus-visible:border-brand-blue focus-visible:outline-none focus-visible:shadow-focus'

  return (
    <div className="mt-s-5">
      <div className="flex flex-wrap items-center justify-between gap-s-3 border border-line-hairline bg-surface-soft px-s-4 py-s-3">
        <div className="flex items-center gap-s-3">
          <button
            type="button"
            onClick={() => onBack(changed)}
            className="inline-flex items-center gap-s-2 rounded-r-1 border border-line-hairline bg-surface-base px-s-3 py-s-2 lf-body-sm text-fg-2 transition-colors duration-base ease-standard hover:border-line-soft hover:text-fg-1"
          >
            <ChevronLeft size={14} strokeWidth={1.5} />
            확인서 목록
          </button>
          <p className="lf-body-sm text-fg-2">
            받는 사람 <span className="lf-num text-fg-1">{drafts.length}</span>명
            <span className="text-fg-3"> · 발신 {mailStatus?.from ?? LECOMMEND_FROM}</span>
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-s-3">
          <button
            type="button"
            onClick={saveDrafts}
            disabled={busy || drafts.length === 0 || blocked}
            title={blocked ? '받는 사람 이메일·제목·본문·템플릿 중 빈 값이 있어요.' : undefined}
            className={`inline-flex items-center gap-s-2 rounded-r-2 px-s-5 py-s-3 lf-h4 transition-colors duration-base ease-standard disabled:cursor-not-allowed disabled:opacity-50 ${
              drafted.length
                ? 'border border-line-soft bg-surface-base text-fg-1 hover:border-line-strong hover:bg-surface-soft'
                : 'bg-brand-pink text-brand-brown hover:bg-pink-500'
            }`}
          >
            <Mail size={15} strokeWidth={1.6} />
            {busy ? '저장 중…' : `${drafted.length ? '초안 다시 저장' : '초안 저장'} (${drafts.length})`}
          </button>
          {drafted.length ? (
            <button
              type="button"
              disabled={busy || dirty || !mailReady}
              title={
                !mailReady
                  ? '메일 발송(SMTP)이 서버에 아직 설정되지 않았어요.'
                  : dirty
                    ? '초안 저장 후 내용이 바뀌었어요. "초안 다시 저장"을 먼저 눌러 주세요.'
                    : undefined
              }
              onClick={() => setSendFor(sendTargets)}
              className="inline-flex items-center gap-s-2 rounded-r-2 bg-brand-pink px-s-5 py-s-3 lf-h4 text-brand-brown transition-colors duration-base ease-standard hover:bg-pink-500 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Send size={15} strokeWidth={1.6} />
              일괄 발송 ({drafted.length})
            </button>
          ) : null}
        </div>
      </div>

      {dirty ? (
        <ResultBanner ok={false} text='초안 저장 후 제목·본문·받는 사람이 바뀌었어요. 발송하려면 "초안 다시 저장"을 눌러 주세요.' />
      ) : null}

      {result ? (
        <div
          className={`mt-s-4 border-l-2 bg-surface-soft px-s-4 py-s-3 lf-body-sm ${
            result.ok ? 'border-l-brand-blue text-fg-1' : 'border-l-status-danger text-status-danger'
          }`}
        >
          ▪ {result.text}
        </div>
      ) : null}

      {mailStatus && !mailReady ? (
        <ResultBanner
          ok={false}
          text="메일 발송(SMTP)이 서버에 아직 설정되지 않았어요. 초안 저장까지만 되고, 설정 후 발송할 수 있어요."
        />
      ) : null}

      {needsPdf.length ? (
        <section className="mt-s-4 border border-line-hairline p-s-5">
          <p className="lf-h4 text-fg-1">확인서 PDF 템플릿</p>
          <p className="mt-s-1 lf-caption text-fg-3">
            PDF가 아직 없는 확인서 <span className="lf-num text-fg-2">{needsPdf.length}</span>건은 초안을 만들 때 이
            템플릿으로 PDF를 생성해 Salesforce 확인서에 저장한 뒤 첨부해요.
          </p>
          <label className="mt-s-3 block max-w-[520px]">
            <span className="sr-only">확인서 템플릿</span>
            <select
              value={template ?? ''}
              onChange={(e) => setTemplate(e.target.value || null)}
              className="w-full rounded-r-1 border border-line-soft bg-surface-base px-s-3 py-s-2 lf-body-sm text-fg-1 focus-visible:border-brand-blue focus-visible:outline-none focus-visible:shadow-focus"
            >
              <option value="">템플릿 선택…</option>
              {(templates ?? []).map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </label>
          {template && needsPdf[0] ? (
            <button
              type="button"
              onClick={() => window.open(generatePdfUrl(needsPdf[0].quoteId, template), '_blank', 'noopener')}
              className="mt-s-2 inline-flex items-center gap-s-1 rounded-r-1 border border-line-hairline bg-surface-base px-s-2 py-px lf-caption text-fg-2 transition-colors duration-base ease-standard hover:border-line-soft hover:text-fg-1"
            >
              <Eye size={12} strokeWidth={1.6} />
              {needsPdf[0].creator || '첫 건'} PDF 미리보기
            </button>
          ) : null}
          {!template ? <p className="mt-s-2 lf-caption text-status-warning">▪ 템플릿을 골라야 초안을 만들 수 있어요.</p> : null}
          {tplErr ? <p className="mt-s-2 lf-caption text-status-danger">▪ {tplErr}</p> : null}
        </section>
      ) : null}

      <section className="mt-s-4 border border-line-hairline p-s-5">
        <p className="lf-h4 text-fg-1">메일 내용</p>
        <p className="mt-s-1 lf-caption text-fg-3">모든 받는 사람에게 같은 제목·본문이 나가요. 첨부는 크리에이터별 확인서예요.</p>
        <div className="mt-s-4 flex flex-col gap-s-3">
          <label className="block">
            <span className="lf-eyebrow text-fg-3">▪ 보내는 사람</span>
            <input
              type="email"
              value={mailStatus ? `${mailStatus.fromName} <${mailStatus.from}>` : LECOMMEND_FROM}
              disabled
              readOnly
              className="mt-s-2 w-full cursor-not-allowed rounded-r-1 border border-line-hairline bg-surface-soft px-s-3 py-s-2 lf-num lf-body-sm text-fg-2"
            />
            <p className="mt-s-1 lf-caption text-fg-3">
              레코멘드 계정 고정. 서버가 이 계정으로 직접 발송하고, 보낸 메일은 이 계정 보낸편지함에 남아요.
            </p>
          </label>
          <label className="block">
            <span className="lf-eyebrow text-fg-3">▪ 제목</span>
            <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)} className={inputCls} />
          </label>
          <label className="block">
            <span className="lf-eyebrow text-fg-3">▪ 본문</span>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={15}
              className={`${inputCls} resize-y leading-relaxed`}
            />
          </label>
          <div>
            <button
              type="button"
              onClick={() => {
                setSubject(tpl.subject)
                setBody(tpl.body)
              }}
              className="rounded-full border border-line-hairline bg-surface-base px-s-3 py-px lf-caption text-fg-2 transition-colors duration-base ease-standard hover:border-line-soft hover:text-fg-1"
            >
              기본 템플릿으로 되돌리기
            </button>
          </div>
        </div>
      </section>

      <section className="mt-s-4 border border-line-hairline">
        <p className="border-b border-line-hairline px-s-5 py-s-4 lf-h4 text-fg-1">받는 사람 · 첨부</p>
        <ul>
          {drafts.map((d, i) => {
            const st = draftRes.get(d.quoteId)
            return (
              <li
                key={d.quoteId}
                className="grid grid-cols-1 gap-s-3 border-b border-line-hairline px-s-5 py-s-4 last:border-b-0 md:grid-cols-[minmax(140px,1fr)_minmax(220px,2fr)_minmax(240px,2fr)] md:items-start"
              >
                <div className="min-w-0">
                  <p className="lf-body-sm text-fg-1">{d.creator || '(크리에이터 미상)'}</p>
                  {st ? (
                    st.ok ? (
                      <p className="mt-px lf-caption text-brand-blue">초안 생성됨</p>
                    ) : (
                      <p className="mt-px lf-caption text-status-danger">
                        ▪ {humanizeConfirmationError(st.error ?? 'draft_failed')}
                      </p>
                    )
                  ) : null}
                </div>
                <label className="block min-w-0">
                  <span className="sr-only">{d.creator} 받는 사람 이메일</span>
                  <input
                    type="email"
                    value={d.email}
                    onChange={(e) => update(i, { email: e.target.value })}
                    placeholder="수신자 이메일"
                    className="w-full rounded-r-1 border border-line-soft bg-surface-base px-s-3 py-s-2 lf-num lf-body-sm text-fg-1 placeholder:text-fg-3 focus-visible:border-brand-blue focus-visible:outline-none focus-visible:shadow-focus"
                  />
                  {!d.email ? (
                    <span className="mt-px block lf-caption text-status-warning">미등록 — 직접 입력해 주세요.</span>
                  ) : null}
                </label>
                <div className="flex min-w-0 flex-col gap-s-2">
                  <AttachRow
                    draft={d}
                    pendingTemplate={template}
                    onUploaded={() => update(i, { pdfName: d.mailFileName })}
                  />
                  <FileNameInput
                    value={d.fileBase}
                    defaultValue={stripPdf(d.mailFileName)}
                    label={`${d.creator} 첨부 파일명`}
                    onChange={(v) => update(i, { fileBase: v })}
                  />
                </div>
              </li>
            )
          })}
        </ul>
      </section>

      <p className="mt-s-4 flex items-start gap-s-2 lf-caption text-fg-3">
        <span aria-hidden>▪</span>
        <span>
          받는 사람 기본값은 Salesforce 크리에이터 이메일이에요. 제목·본문·받는 사람을 고친 뒤에는 "초안 다시 저장"을
          눌러야 반영돼요.
        </span>
      </p>

      {sendFor ? (
        <SendConfirmModal
          quotes={sendFor}
          subject={subject}
          onClose={() => setSendFor(null)}
          onDone={(r) => {
            setSendFor(null)
            onBack(true, r)
          }}
        />
      ) : null}
    </div>
  )
}

/** 초안 첨부 PDF — SF 저장분 표시 + 수동 업로드(SF 에 저장해 동일 소스 유지). */
function AttachRow({
  draft,
  pendingTemplate,
  onUploaded,
}: {
  draft: EmailDraft
  /** PDF 가 없을 때 초안 생성 시 자동 생성에 쓸 템플릿. */
  pendingTemplate?: string | null
  onUploaded: (name: string) => void
}) {
  const ref = useRef<HTMLInputElement | null>(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const pick = (f: File | null) => {
    if (!f) return
    setErr(null)
    if (!/\.pdf$/i.test(f.name) && f.type !== 'application/pdf') {
      setErr('PDF 파일만 올릴 수 있어요.')
      return
    }
    if (f.size > 25 * 1024 * 1024) {
      setErr('25MB 이하 PDF만 가능해요.')
      return
    }
    const reader = new FileReader()
    reader.onload = async () => {
      const res = String(reader.result)
      const base64 = res.includes(',') ? res.slice(res.indexOf(',') + 1) : res
      setBusy(true)
      try {
        const r = await uploadQuotePdf(draft.quoteId, f.name, base64)
        onUploaded(r.pdfName)
      } catch (e) {
        setErr(e instanceof ConfirmationError ? humanizeConfirmationError(e.message) : '업로드 실패')
      } finally {
        setBusy(false)
        if (ref.current) ref.current.value = ''
      }
    }
    reader.readAsDataURL(f)
  }

  return (
    <div className="flex flex-wrap items-center gap-s-2">
      <span className="lf-eyebrow text-fg-3">▪ 첨부</span>
      {draft.pdfName ? (
        <span className="inline-flex items-center gap-s-1 rounded-r-1 border border-line-hairline bg-surface-soft px-s-2 py-px lf-caption text-fg-2">
          <FileCheck2 size={13} strokeWidth={1.6} className="text-brand-blue" />
          {draft.pdfName}
        </span>
      ) : pendingTemplate ? (
        <span className="lf-caption text-fg-2">초안 생성 시 자동 생성 · {templateSubtitleC(pendingTemplate)}</span>
      ) : (
        <span className="lf-caption text-status-warning">PDF 없음 — 템플릿 선택 필요</span>
      )}
      <input
        ref={ref}
        type="file"
        accept="application/pdf,.pdf"
        hidden
        onChange={(e) => pick(e.target.files?.[0] ?? null)}
      />
      <button
        type="button"
        disabled={busy}
        onClick={() => ref.current?.click()}
        className="inline-flex items-center gap-s-1 rounded-r-1 border border-line-hairline bg-surface-base px-s-2 py-px lf-caption text-fg-2 transition-colors duration-base ease-standard hover:border-line-soft hover:text-fg-1 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <Upload size={12} strokeWidth={1.6} />
        {busy ? '업로드 중…' : draft.pdfName ? 'PDF 교체' : 'PDF 업로드'}
      </button>
      <span className="lf-caption text-fg-3">(Salesforce에 저장)</span>
      {err ? <span className="lf-caption text-status-danger">▪ {err}</span> : null}
    </div>
  )
}

/** 일괄 발송 확인 — 받는 사람 목록 확인 후 실제 발송(되돌릴 수 없음). */
function SendConfirmModal({
  quotes,
  subject,
  onClose,
  onDone,
}: {
  quotes: QuoteRow[]
  /** 컴포즈 화면에서 연 경우 제목 표시(목록에서 열면 초안에 저장된 제목). */
  subject?: string
  onClose: () => void
  onDone: (r: { ok: boolean; text: string }) => void
}) {
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const send = async () => {
    setBusy(true)
    setErr(null)
    try {
      const r = await sendDrafts(quotes.map((q) => q.id))
      const failed = r.results.filter((x) => x.status === 'failed')
      const byId = new Map(quotes.map((q) => [q.id, q.creator ?? q.name]))
      onDone({
        ok: failed.length === 0,
        text:
          `${r.summary.sent}건 발송 완료 (${LECOMMEND_FROM})` +
          (failed.length
            ? ` · ${failed.length}건 실패: ` +
              failed.map((f) => `${byId.get(f.quoteId) ?? f.quoteId} — ${humanizeConfirmationError(f.error ?? 'send_failed')}`).join(', ')
            : ''),
      })
    } catch (e) {
      setErr(e instanceof ConfirmationError ? humanizeConfirmationError(e.message) : '발송 실패')
      setBusy(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-brand-brown/40 p-s-5"
      role="dialog"
      aria-modal="true"
      aria-labelledby="send-confirm-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose()
      }}
    >
      <div className="mt-s-8 w-full max-w-[560px] border border-line-hairline bg-surface-base shadow-pop">
        <div className="border-b border-line-hairline px-s-5 py-s-4">
          <p className="lf-eyebrow text-fg-3">▪ 일괄 발송</p>
          <h3 id="send-confirm-title" className="mt-s-1 lf-h3 text-fg-1">
            {`${quotes.length}명에게 확인서 메일을 보낼까요?`}
          </h3>
          <p className="mt-s-2 lf-body-sm text-fg-2">
            {LECOMMEND_FROM}에서 바로 발송되고, 보낸 메일은 취소할 수 없어요.
          </p>
          {subject ? <p className="mt-s-2 lf-caption text-fg-3">제목 · {subject}</p> : null}
        </div>
        <ul className="max-h-[50vh] overflow-y-auto">
          {quotes.map((q) => (
            <li
              key={q.id}
              className="flex items-center justify-between gap-s-3 border-b border-line-hairline px-s-5 py-s-3 last:border-b-0"
            >
              <span className="min-w-0 truncate lf-body-sm text-fg-1">{q.creator || q.name}</span>
              <span className="shrink-0 lf-num lf-caption text-fg-2">{q.mail?.to ?? q.email ?? '—'}</span>
            </li>
          ))}
        </ul>
        <div className="flex items-center justify-between gap-s-3 border-t border-line-hairline px-s-5 py-s-4">
          <p className="lf-caption text-status-danger">{err ? `▪ ${err}` : ''}</p>
          <div className="flex items-center gap-s-3">
            <button
              type="button"
              disabled={busy}
              onClick={onClose}
              className="rounded-r-1 border border-line-hairline bg-surface-base px-s-4 py-s-2 lf-body-sm text-fg-2 transition-colors duration-base ease-standard hover:border-line-soft hover:text-fg-1 disabled:opacity-50"
            >
              취소
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={send}
              className="inline-flex items-center gap-s-2 rounded-r-2 bg-brand-pink px-s-5 py-s-2 lf-h4 text-brand-brown transition-colors duration-base ease-standard hover:bg-pink-500 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Send size={14} strokeWidth={1.6} />
              {busy ? '발송 중…' : `${quotes.length}건 발송`}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* 판매상품 추가 모달 — 제품 다중 선택 → 제품별 폼                              */
/* -------------------------------------------------------------------------- */

function AddProductModal({ onClose, onAdd }: { onClose: () => void; onAdd: (ds: DraftProduct[]) => void }) {
  const [step, setStep] = useState<'product' | 'form'>('product')
  const [products, setProducts] = useState<ProductRow[]>([])
  const [fields, setFields] = useState<ProductFields | null>(null)

  // picklist 옵션 로드 (한 번).
  useEffect(() => {
    getProductFields()
      .then(setFields)
      .catch(() => setFields({ departments: [], businessCategories: [], itemCategories: [] }))
  }, [])

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-brand-brown/40 p-s-5"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="mt-s-7 w-full max-w-[900px] border border-line-hairline bg-surface-base shadow-pop">
        <div className="flex items-center justify-between border-b border-line-hairline px-s-5 py-s-4">
          <div>
            <p className="lf-eyebrow text-fg-3">
              ▪ 판매상품 추가 · {step === 'product' ? '제품 선택' : '세부 입력'}
            </p>
            <h3 className="mt-s-1 lf-h3 text-fg-1">
              {step === 'product' ? '어떤 상품인가요?' : `세부 입력 · ${products.length}개`}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="닫기"
            className="inline-flex items-center justify-center rounded-r-1 border border-line-hairline p-s-2 text-fg-2 transition-colors duration-base ease-standard hover:border-line-soft hover:text-fg-1"
          >
            <XIcon size={16} strokeWidth={1.6} />
          </button>
        </div>

        {step === 'product' ? (
          <ProductPicker
            onCancel={onClose}
            onNext={(ps) => {
              setProducts(ps)
              setStep('form')
            }}
          />
        ) : (
          <ProductForm
            products={products}
            fields={fields}
            onBack={() => setStep('product')}
            onAdd={(ds) => {
              onAdd(ds)
              onClose()
            }}
          />
        )}
      </div>
    </div>
  )
}

function ProductPicker({ onCancel, onNext }: { onCancel: () => void; onNext: (ps: ProductRow[]) => void }) {
  const [query, setQuery] = useState('')
  const [search, setSearch] = useState('')
  const [rows, setRows] = useState<ProductRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // pbeId → ProductRow (검색이 바뀌어도 선택 유지).
  const [sel, setSel] = useState<Map<string, ProductRow>>(new Map())

  useEffect(() => {
    const t = setTimeout(() => setSearch(query.trim()), 300)
    return () => clearTimeout(t)
  }, [query])

  useEffect(() => {
    let live = true
    setLoading(true)
    setError(null)
    searchProducts(search)
      .then((r) => {
        if (!live) return
        setRows(r.rows)
        setLoading(false)
      })
      .catch((e: unknown) => {
        if (!live) return
        setError(e instanceof ConfirmationError ? humanizeConfirmationError(e.message) : '제품 조회 실패')
        setLoading(false)
      })
    return () => {
      live = false
    }
  }, [search])

  const toggle = (p: ProductRow) =>
    setSel((prev) => {
      const next = new Map(prev)
      if (next.has(p.pbeId)) next.delete(p.pbeId)
      else next.set(p.pbeId, p)
      return next
    })

  return (
    <div className="flex flex-col">
      <div className="p-s-5">
        <label className="relative block">
          <span className="pointer-events-none absolute left-s-3 top-1/2 -translate-y-1/2 text-fg-3">
            <Search size={16} strokeWidth={1.6} />
          </span>
          <input
            type="text"
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="제품 검색 — 제품 이름 (예: 레코멘드, 커뮤니티)"
            className="w-full rounded-r-1 border border-line-soft bg-surface-base py-s-3 pl-s-9 pr-s-4 lf-body text-fg-1 placeholder:text-fg-3 focus-visible:border-brand-blue focus-visible:outline-none focus-visible:shadow-focus"
          />
        </label>
      </div>

      {error ? <p className="px-s-5 pb-s-3 lf-body-sm text-status-danger">▪ {error}</p> : null}

      <div className="max-h-[44vh] overflow-y-auto border-t border-line-hairline">
        {loading ? (
          <p className="px-s-5 py-s-6 text-center lf-body-sm text-fg-3">불러오는 중…</p>
        ) : rows.length === 0 ? (
          <p className="px-s-5 py-s-6 text-center lf-body-sm text-fg-3">검색 결과가 없어요.</p>
        ) : (
          <ul className="flex flex-col">
            {rows.map((p) => {
              const active = sel.has(p.pbeId)
              return (
                <li key={p.pbeId}>
                  <button
                    type="button"
                    onClick={() => toggle(p)}
                    className={`flex w-full items-center gap-s-3 border-b border-line-hairline px-s-5 py-s-3 text-left transition-colors duration-base ease-standard last:border-b-0 focus-visible:outline-none focus-visible:shadow-focus ${
                      active ? 'bg-pink-50' : 'hover:bg-surface-soft'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={active}
                      readOnly
                      tabIndex={-1}
                      className="h-4 w-4 flex-shrink-0 accent-brand-pink"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate lf-body-sm text-fg-1">{p.name}</span>
                      {p.family ? <span className="lf-caption text-fg-3">{p.family}</span> : null}
                    </span>
                    {p.listPrice ? (
                      <span className="lf-num lf-caption text-fg-3">정가 {p.listPrice.toLocaleString()}</span>
                    ) : null}
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </div>

      <div className="flex items-center justify-between gap-s-3 border-t border-line-hairline px-s-5 py-s-4">
        <p className="lf-body-sm text-fg-2">
          {sel.size ? (
            <>
              <span className="lf-num text-fg-1">{sel.size}</span>개 선택됨
            </>
          ) : (
            <span className="text-fg-3">제품을 하나 이상 선택하세요.</span>
          )}
        </p>
        <div className="flex items-center gap-s-3">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-r-1 border border-line-hairline bg-surface-base px-s-4 py-s-2 lf-body-sm text-fg-2 transition-colors duration-base ease-standard hover:border-line-soft hover:text-fg-1"
          >
            취소
          </button>
          <button
            type="button"
            disabled={sel.size === 0}
            onClick={() => onNext([...sel.values()])}
            className="rounded-r-2 bg-brand-pink px-s-5 py-s-2 lf-h4 text-brand-brown transition-colors duration-base ease-standard hover:bg-pink-500 disabled:cursor-not-allowed disabled:opacity-50"
          >
            다음{sel.size ? ` (${sel.size})` : ''}
          </button>
        </div>
      </div>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* 제품별 세부 입력 폼                                                           */
/* -------------------------------------------------------------------------- */

interface RowState {
  department: string
  brandItem: string
  optionName: string
  businessCategory: string
  itemCategory: string
  creator: CreatorRow | null
  contentsFee: string
  pricePrj: string
  prepayDate: string
  deferredDate: string
  uploadDate: string
}

function emptyRow(product: ProductRow): RowState {
  return {
    department: product.family ?? '',
    brandItem: '',
    optionName: '',
    businessCategory: '',
    itemCategory: '',
    creator: null,
    contentsFee: '',
    pricePrj: '',
    prepayDate: '',
    deferredDate: '',
    uploadDate: '',
  }
}

/**
 * 판매단가(VAT별도) = 콘텐츠제작단가 + 운영관리비 (SF 검증룰 Total_Price_Detail_Error).
 * 자동 계산해 UnitPrice 로 저장하면 규칙을 항상 만족한다. (외주마크업/매체비/매체수수료는
 * 레코멘드 미사용 — 필요해지면 항목 추가.)
 */
function rowUnitPrice(r: RowState): number {
  return toIntC(r.contentsFee) + toIntC(r.pricePrj)
}

function ProductForm({
  products,
  fields,
  onBack,
  onAdd,
}: {
  products: ProductRow[]
  fields: ProductFields | null
  onBack: () => void
  onAdd: (ds: DraftProduct[]) => void
}) {
  const [rows, setRows] = useState<RowState[]>(() => products.map(emptyRow))

  const update = (i: number, patch: Partial<RowState>) =>
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)))

  // 브랜드 제품명 필수 + 판매단가(=내역 합) > 0.
  const invalid = rows.some((r) => r.brandItem.trim() === '' || rowUnitPrice(r) <= 0)

  const submit = () => {
    if (invalid) return
    const drafts: DraftProduct[] = products.map((product, i) => {
      const r = rows[i]
      return {
        key: `${product.pbeId}-${Date.now()}-${i}`,
        product,
        department: r.department.trim(),
        brandItem: r.brandItem.trim(),
        optionName: r.optionName.trim(),
        businessCategory: r.businessCategory,
        itemCategory: r.itemCategory,
        creator: r.creator,
        unitPrice: String(rowUnitPrice(r)),
        contentsFee: r.contentsFee.trim(),
        pricePrj: r.pricePrj.trim(),
        prepayDate: r.prepayDate,
        deferredDate: r.deferredDate,
        uploadDate: r.uploadDate,
      }
    })
    onAdd(drafts)
  }

  const departments = fields?.departments ?? []
  const businessCats = fields?.businessCategories ?? []

  return (
    <div className="flex flex-col">
      <div className="max-h-[56vh] space-y-s-5 overflow-y-auto p-s-5">
        {products.map((product, i) => {
          const r = rows[i]
          // 선택한 사업 카테고리에 허용되는 제품 카테고리만.
          const itemOptions = (fields?.itemCategories ?? [])
            .filter((ic) => !r.businessCategory || ic.businesses.includes(r.businessCategory))
            .map((ic) => ic.value)
          return (
            <section key={product.pbeId} className="border border-line-hairline p-s-4">
              <p className="lf-h4 text-fg-1">
                {products.length > 1 ? <span className="lf-num text-fg-3">{i + 1}. </span> : null}
                {product.name}
              </p>
              <div className="mt-s-4 grid grid-cols-1 gap-s-4 sm:grid-cols-2">
                <SelectField
                  label="귀속 부서"
                  value={r.department}
                  onChange={(v) => update(i, { department: v })}
                  options={departments}
                />
                <TextField
                  label="브랜드 제품명"
                  required
                  value={r.brandItem}
                  onChange={(v) => update(i, { brandItem: v })}
                  placeholder="예: 해단식 페어웰 키트"
                />
                <TextField
                  label="옵션명"
                  value={r.optionName}
                  onChange={(v) => update(i, { optionName: v })}
                />
                <CreatorField value={r.creator} onChange={(c) => update(i, { creator: c })} />
                <SelectField
                  label="사업 카테고리"
                  value={r.businessCategory}
                  onChange={(v) =>
                    // 사업 바뀌면 더 이상 유효하지 않은 제품 카테고리는 초기화.
                    update(i, {
                      businessCategory: v,
                      itemCategory: (fields?.itemCategories ?? []).some(
                        (ic) => ic.value === r.itemCategory && ic.businesses.includes(v),
                      )
                        ? r.itemCategory
                        : '',
                    })
                  }
                  options={businessCats}
                />
                <SelectField
                  label="제품 카테고리"
                  value={r.itemCategory}
                  onChange={(v) => update(i, { itemCategory: v })}
                  options={itemOptions}
                  disabled={!r.businessCategory}
                  placeholder={r.businessCategory ? '선택' : '사업 카테고리 먼저'}
                />
                <NumberField
                  label="콘텐츠 제작단가"
                  value={r.contentsFee}
                  onChange={(v) => update(i, { contentsFee: v })}
                />
                <NumberField
                  label="프로젝트 운영관리비 (마크업)"
                  value={r.pricePrj}
                  onChange={(v) => update(i, { pricePrj: v })}
                />
                <ComputedPrice value={rowUnitPrice(r)} />
                <DateField
                  label="업로드 일정"
                  value={r.uploadDate}
                  onChange={(v) => update(i, { uploadDate: v })}
                />
                <DateField
                  label="크리에이터 선입금 정산일"
                  value={r.prepayDate}
                  onChange={(v) => update(i, { prepayDate: v })}
                />
                <DateField
                  label="크리에이터 후입금 정산일"
                  value={r.deferredDate}
                  onChange={(v) => update(i, { deferredDate: v })}
                />
              </div>
            </section>
          )
        })}
      </div>

      <div className="flex items-center justify-between gap-s-3 border-t border-line-hairline px-s-5 py-s-4">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-s-2 rounded-r-1 border border-line-hairline bg-surface-base px-s-4 py-s-2 lf-body-sm text-fg-2 transition-colors duration-base ease-standard hover:border-line-soft hover:text-fg-1"
        >
          <ChevronLeft size={14} strokeWidth={1.5} />
          뒤로
        </button>
        <div className="flex items-center gap-s-3">
          {invalid ? (
            <span className="lf-caption text-fg-3">
              브랜드 제품명 + 판매단가(콘텐츠제작단가·운영관리비 중 하나 이상)는 필수예요.
            </span>
          ) : null}
          <button
            type="button"
            disabled={invalid}
            onClick={submit}
            className="inline-flex items-center gap-s-2 rounded-r-2 bg-brand-pink px-s-5 py-s-2 lf-h4 text-brand-brown transition-colors duration-base ease-standard hover:bg-pink-500 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Plus size={14} strokeWidth={1.8} />
            {products.length > 1 ? `${products.length}개 추가 (미리보기)` : '추가 (미리보기)'}
          </button>
        </div>
      </div>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* 크리에이터 검색 필드                                                          */
/* -------------------------------------------------------------------------- */

/* -------------------------------------------------------------------------- */
/* 크리에이터 지정 — 크리에이터 없는 판매상품(대량 시딩 등) → 크리에이터별 판매상품 */
/* -------------------------------------------------------------------------- */

interface AssignRow {
  creator: CreatorRow
  contentsFee: string
  pricePrj: string
}

/**
 * 크리에이터 없는 판매상품에 크리에이터 지정. 1번째 = 이 판매상품, 나머지 = 같은 내용으로 복제(에이지투웨니스 방식).
 * 단가는 원본을 복제해 두고 크리에이터별로 수정 가능. 판매단가 = 콘텐츠제작단가 + 운영관리비 + (원본의 나머지 가격 항목).
 * 반영 전 서버 미리보기(dry-run)로 검증 → 확인 → SF 반영.
 */
function AssignCreatorsModal({
  opp,
  item,
  onClose,
  onDone,
}: {
  opp: OpportunityRow
  item: LineItemRow
  onClose: () => void
  /** 반영 후 — 영향받은 판매상품 id(원본 + 새로 만든 것). */
  onDone: (oliIds: string[], notice: { ok: boolean; text: string }) => void
}) {
  const baseContents = item.contentsFee ?? 0
  const basePrj = item.pricePrj ?? 0
  // 원본 판매단가 중 콘텐츠제작단가·운영관리비 외 나머지(외주마크업·매체비·매체수수료) — 복제분에도 그대로.
  const otherPrice = (item.unitPrice ?? 0) - baseContents - basePrj
  const [rows, setRows] = useState<AssignRow[]>([])
  const [step, setStep] = useState<'edit' | 'confirm' | 'saving' | 'done'>('edit')
  const [err, setErr] = useState<string | null>(null)
  const [results, setResults] = useState<AssignResult[] | null>(null)
  const locked = /세금계산서/.test(opp.stage ?? '')

  const add = (c: CreatorRow | null) => {
    if (!c || rows.some((r) => r.creator.id === c.id)) return
    setRows((prev) => [...prev, { creator: c, contentsFee: String(baseContents), pricePrj: String(basePrj) }])
  }
  const patchRow = (i: number, p: Partial<AssignRow>) => setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...p } : r)))
  const unitOf = (r: AssignRow) => toIntC(r.contentsFee) + toIntC(r.pricePrj) + otherPrice
  const payload = () =>
    rows.map((r) => ({ creatorId: r.creator.id, contentsFee: toIntC(r.contentsFee), pricePrj: toIntC(r.pricePrj) }))

  // 1) 서버 미리보기로 검증(SF 쓰기 없음) → 확인 단계.
  const review = async () => {
    setErr(null)
    try {
      await previewAssignCreators(opp.oppId, item.id, payload())
      setStep('confirm')
    } catch (e) {
      setErr(e instanceof ConfirmationError ? humanizeConfirmationError(e.message) : '확인 실패')
    }
  }
  // 2) ⚠️ SF 반영.
  const save = async () => {
    setStep('saving')
    setErr(null)
    try {
      const r = await assignCreators(opp.oppId, item.id, payload())
      setResults(r.results)
      setStep('done')
      const ids = r.results.filter((x) => x.status === 'ok' && x.id).map((x) => x.id!)
      onDone(ids, {
        ok: r.summary.failed === 0,
        text:
          `크리에이터 ${r.summary.ok}명 지정 완료` +
          (r.summary.failed ? `, ${r.summary.failed}명 실패` : '') +
          ' (Salesforce 판매상품 반영).',
      })
    } catch (e) {
      setErr(e instanceof ConfirmationError ? humanizeConfirmationError(e.message) : '저장 실패')
      setStep('confirm')
    }
  }

  const numCls =
    'w-[120px] rounded-r-1 border border-line-soft bg-surface-base px-s-2 py-s-1 text-right lf-num lf-body-sm text-fg-1 focus-visible:border-brand-blue focus-visible:outline-none focus-visible:shadow-focus disabled:bg-surface-soft disabled:text-fg-2'

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-brand-brown/40 p-s-5"
      role="dialog"
      aria-modal="true"
      aria-labelledby="assign-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && step !== 'saving') onClose()
      }}
    >
      <div className="mt-s-6 w-full max-w-[760px] border border-line-hairline bg-surface-base shadow-pop">
        <div className="flex items-start justify-between gap-s-3 border-b border-line-hairline px-s-5 py-s-4">
          <div className="min-w-0">
            <p className="lf-eyebrow text-fg-3">▪ 크리에이터 지정</p>
            <h3 id="assign-title" className="mt-s-1 lf-h3 text-fg-1">
              {item.brandItem || item.product || '판매상품'}
            </h3>
            <p className="mt-s-1 lf-caption text-fg-3">
              {item.product} · 판매단가 <span className="lf-num">{wonN(item.unitPrice)}</span>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={step === 'saving'}
            aria-label="닫기"
            className="inline-flex items-center justify-center rounded-r-1 border border-line-hairline p-s-2 text-fg-2 transition-colors duration-base ease-standard hover:border-line-soft hover:text-fg-1 disabled:opacity-50"
          >
            <XIcon size={16} strokeWidth={1.6} />
          </button>
        </div>

        <div className="flex flex-col gap-s-4 px-s-5 py-s-4">
          <p className="lf-body-sm text-fg-2">
            첫 번째 크리에이터는 이 판매상품에 지정하고, 나머지는 같은 내용으로 판매상품을 복제해 1명씩 지정해요.
            단가는 원본 값으로 채워 두니 필요하면 크리에이터별로 고쳐 주세요.
          </p>
          {locked ? (
            <ResultBanner ok={false} text="기회가 '세금계산서' 단계라 가격 항목 변경은 재무팀 권한이 필요할 수 있어요." />
          ) : null}

          {step === 'edit' ? <CreatorField value={null} onChange={add} resetOnPick /> : null}

          <div className="overflow-x-auto border border-line-hairline">
            <table className="w-full border-separate border-spacing-0">
              <thead>
                <tr>
                  <Th>크리에이터</Th>
                  <Th>반영</Th>
                  <Th align="right">콘텐츠제작단가</Th>
                  <Th align="right">운영관리비</Th>
                  <Th align="right">판매단가</Th>
                  <Th>{step === 'done' ? '결과' : ''}</Th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <Td colSpan={6} className="text-center text-fg-3">
                      위에서 크리에이터를 검색해 추가해 주세요.
                    </Td>
                  </tr>
                ) : (
                  rows.map((r, i) => {
                    const res = results?.find((x) => x.creatorId === r.creator.id)
                    return (
                      <tr key={r.creator.id}>
                        <Td className="text-fg-1">
                          <span className="block">{r.creator.name}</span>
                          {r.creator.email ? <span className="lf-caption text-fg-3">{r.creator.email}</span> : null}
                        </Td>
                        <Td className="lf-caption text-fg-2">{i === 0 ? '이 판매상품에 지정' : '판매상품 복제'}</Td>
                        <Td align="right">
                          <input
                            inputMode="numeric"
                            value={r.contentsFee}
                            disabled={step !== 'edit'}
                            onChange={(e) => patchRow(i, { contentsFee: e.target.value })}
                            aria-label={`${r.creator.name} 콘텐츠제작단가`}
                            className={numCls}
                          />
                        </Td>
                        <Td align="right">
                          <input
                            inputMode="numeric"
                            value={r.pricePrj}
                            disabled={step !== 'edit'}
                            onChange={(e) => patchRow(i, { pricePrj: e.target.value })}
                            aria-label={`${r.creator.name} 운영관리비`}
                            className={numCls}
                          />
                        </Td>
                        <Td align="right" className="lf-num text-fg-1">
                          {won(String(unitOf(r)))}
                        </Td>
                        <Td>
                          {res ? (
                            res.status === 'ok' ? (
                              <span className="lf-caption text-brand-blue">반영됨</span>
                            ) : (
                              <span className="max-w-[200px] whitespace-normal lf-caption text-status-danger">
                                실패 · {res.error}
                              </span>
                            )
                          ) : step === 'edit' ? (
                            <button
                              type="button"
                              onClick={() => setRows((prev) => prev.filter((_, idx) => idx !== i))}
                              aria-label={`${r.creator.name} 빼기`}
                              className="text-fg-3 hover:text-fg-1"
                            >
                              <Trash2 size={14} strokeWidth={1.6} />
                            </button>
                          ) : null}
                        </Td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
          {otherPrice ? (
            <p className="lf-caption text-fg-3">
              판매단가에는 원본의 다른 가격 항목(외주마크업·매체비·매체수수료) <span className="lf-num">{wonN(otherPrice)}</span>이
              함께 들어가요.
            </p>
          ) : null}
          {step === 'confirm' ? (
            <ResultBanner
              ok
              text={`Salesforce에 반영해요: 판매상품 1건 수정${rows.length > 1 ? ` + ${rows.length - 1}건 추가` : ''}. 반영 후에는 SF에서 되돌려야 해요.`}
            />
          ) : null}
          {err ? <p className="lf-caption text-status-danger">▪ {err}</p> : null}
        </div>

        <div className="flex items-center justify-end gap-s-3 border-t border-line-hairline px-s-5 py-s-4">
          {step === 'done' ? (
            <button
              type="button"
              onClick={onClose}
              className="rounded-r-1 border border-line-hairline bg-surface-base px-s-4 py-s-2 lf-body-sm text-fg-2 transition-colors duration-base ease-standard hover:border-line-soft hover:text-fg-1"
            >
              닫기
            </button>
          ) : (
            <>
              <button
                type="button"
                disabled={step === 'saving'}
                onClick={step === 'confirm' ? () => setStep('edit') : onClose}
                className="rounded-r-1 border border-line-hairline bg-surface-base px-s-4 py-s-2 lf-body-sm text-fg-2 transition-colors duration-base ease-standard hover:border-line-soft hover:text-fg-1 disabled:opacity-50"
              >
                {step === 'confirm' ? '뒤로' : '취소'}
              </button>
              <button
                type="button"
                disabled={rows.length === 0 || step === 'saving'}
                onClick={step === 'edit' ? review : save}
                className="inline-flex items-center gap-s-2 rounded-r-2 bg-brand-pink px-s-5 py-s-2 lf-h4 text-brand-brown transition-colors duration-base ease-standard hover:bg-pink-500 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {step === 'edit' ? `${rows.length}명 지정` : step === 'saving' ? '반영 중…' : 'Salesforce에 반영'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function CreatorField({
  value,
  onChange,
  resetOnPick = false,
}: {
  value: CreatorRow | null
  onChange: (c: CreatorRow | null) => void
  /** 여러 명 추가용 — 고른 뒤 검색창을 비운다(value 는 항상 null 로 둔다). */
  resetOnPick?: boolean
}) {
  const [query, setQuery] = useState('')
  const [rows, setRows] = useState<CreatorRow[]>([])
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const boxRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (value) return
    const q = query.trim()
    if (q.length < 2) {
      setRows([])
      return
    }
    let live = true
    setLoading(true)
    const t = setTimeout(() => {
      searchCreators(q)
        .then((r) => live && setRows(r.rows))
        .catch(() => live && setRows([]))
        .finally(() => live && setLoading(false))
    }, 300)
    return () => {
      live = false
      clearTimeout(t)
    }
  }, [query, value])

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [])

  return (
    <div ref={boxRef}>
      <p className="lf-eyebrow text-fg-3">▪ 크리에이터</p>
      <div className="relative mt-s-2">
        {value ? (
          <div className="flex items-center justify-between gap-s-2 rounded-r-1 border border-line-soft bg-surface-soft px-s-3 py-s-2">
            <span className="min-w-0">
              <span className="block truncate lf-body-sm text-fg-1">{value.name}</span>
              {value.email ? <span className="lf-caption text-fg-3">{value.email}</span> : null}
            </span>
            <button
              type="button"
              onClick={() => {
                onChange(null)
                setQuery('')
              }}
              aria-label="크리에이터 지우기"
              className="flex-shrink-0 text-fg-3 hover:text-fg-1"
            >
              <XIcon size={14} strokeWidth={1.6} />
            </button>
          </div>
        ) : (
          <>
            <input
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value)
                setOpen(true)
              }}
              onFocus={() => setOpen(true)}
              placeholder="이름으로 검색 (2자 이상)"
              className="w-full rounded-r-1 border border-line-soft bg-surface-base px-s-3 py-s-2 lf-body-sm text-fg-1 placeholder:text-fg-3 focus-visible:border-brand-blue focus-visible:outline-none focus-visible:shadow-focus"
            />
            {open && (query.trim().length >= 2 || rows.length) ? (
              <div className="absolute z-10 mt-s-1 max-h-[200px] w-full overflow-y-auto border border-line-soft bg-surface-base shadow-pop">
                {loading ? (
                  <p className="px-s-3 py-s-2 lf-caption text-fg-3">검색 중…</p>
                ) : rows.length === 0 ? (
                  <p className="px-s-3 py-s-2 lf-caption text-fg-3">결과 없음</p>
                ) : (
                  rows.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => {
                        onChange(c)
                        setOpen(false)
                        if (resetOnPick) {
                          setQuery('')
                          setRows([])
                        }
                      }}
                      className="flex w-full flex-col items-start border-b border-line-hairline px-s-3 py-s-2 text-left last:border-b-0 hover:bg-surface-soft"
                    >
                      <span className="lf-body-sm text-fg-1">{c.name}</span>
                      {c.email ? <span className="lf-caption text-fg-3">{c.email}</span> : null}
                    </button>
                  ))
                )}
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* atoms                                                                       */
/* -------------------------------------------------------------------------- */

function SelectField({
  label,
  value,
  onChange,
  options,
  required,
  placeholder,
  disabled,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  options: string[]
  required?: boolean
  placeholder?: string
  disabled?: boolean
}) {
  return (
    <label className="block">
      <span className="lf-eyebrow text-fg-3">
        ▪ {label}
        {required ? <span className="text-brand-pink"> *</span> : null}
      </span>
      <select
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="mt-s-2 w-full rounded-r-1 border border-line-soft bg-surface-base px-s-3 py-s-2 lf-body-sm text-fg-1 focus-visible:border-brand-blue focus-visible:outline-none focus-visible:shadow-focus disabled:cursor-not-allowed disabled:opacity-50"
      >
        <option value="">{placeholder ?? '선택'}</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </label>
  )
}

function TextField({
  label,
  value,
  onChange,
  placeholder,
  required,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  placeholder?: string
  required?: boolean
}) {
  return (
    <label className="block">
      <span className="lf-eyebrow text-fg-3">
        ▪ {label}
        {required ? <span className="text-brand-pink"> *</span> : null}
      </span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-s-2 w-full rounded-r-1 border border-line-soft bg-surface-base px-s-3 py-s-2 lf-body-sm text-fg-1 placeholder:text-fg-3 focus-visible:border-brand-blue focus-visible:outline-none focus-visible:shadow-focus"
      />
    </label>
  )
}

function NumberField({
  label,
  value,
  onChange,
  required,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  required?: boolean
}) {
  return (
    <label className="block">
      <span className="lf-eyebrow text-fg-3">
        ▪ {label}
        {required ? <span className="text-brand-pink"> *</span> : null}
      </span>
      <input
        type="number"
        inputMode="numeric"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="0"
        className="mt-s-2 w-full rounded-r-1 border border-line-soft bg-surface-base px-s-3 py-s-2 lf-num lf-body-sm text-fg-1 placeholder:text-fg-3 focus-visible:border-brand-blue focus-visible:outline-none focus-visible:shadow-focus"
      />
    </label>
  )
}

/** 판매단가(VAT별도) — 내역 합으로 자동 계산된 읽기전용 표시. SF 검증룰 충족. */
function ComputedPrice({ value }: { value: number }) {
  return (
    <div>
      <span className="lf-eyebrow text-fg-3">
        ▪ 판매단가 (VAT별도)<span className="text-brand-pink"> *</span>
      </span>
      <div className="mt-s-2 flex items-center rounded-r-1 border border-line-hairline bg-surface-soft px-s-3 py-s-2">
        <span className="lf-num lf-body-sm text-fg-1">₩{value.toLocaleString()}</span>
      </div>
      <p className="mt-s-1 lf-caption text-fg-3">콘텐츠제작단가 + 운영관리비 (자동)</p>
    </div>
  )
}

function DateField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <label className="block">
      <span className="lf-eyebrow text-fg-3">▪ {label}</span>
      <input
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-s-2 w-full rounded-r-1 border border-line-soft bg-surface-base px-s-3 py-s-2 lf-num lf-body-sm text-fg-1 focus-visible:border-brand-blue focus-visible:outline-none focus-visible:shadow-focus"
      />
    </label>
  )
}

function Th({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return (
    <th
      className={`whitespace-nowrap border-b border-line-soft bg-surface-soft px-s-3 py-s-2 lf-caption text-fg-3 ${
        align === 'right' ? 'text-right' : 'text-left'
      }`}
    >
      {children}
    </th>
  )
}

function Td({
  children,
  className = '',
  colSpan,
  align = 'left',
}: {
  children: React.ReactNode
  className?: string
  colSpan?: number
  align?: 'left' | 'right'
}) {
  return (
    <td
      colSpan={colSpan}
      className={`whitespace-nowrap border-b border-line-hairline px-s-3 py-s-3 align-top lf-body-sm ${
        align === 'right' ? 'text-right' : ''
      } ${className}`}
    >
      {children}
    </td>
  )
}

/** 숫자 문자열 → 정수(쉼표 제거). 빈값/NaN → 0. */
function toIntC(v: string): number {
  const s = String(v ?? '').replace(/,/g, '').trim()
  if (!s) return 0
  const n = Number(s)
  return Number.isFinite(n) ? Math.trunc(n) : 0
}

/** 문자열 금액(폼) → ₩ 표기. 빈값/0 → —. */
function won(v: string): string {
  const n = Number(v)
  return v && Number.isFinite(n) && n !== 0 ? `₩${n.toLocaleString()}` : '—'
}
/** 숫자 금액(SF) → ₩ 표기. */
function wonN(v: number | null): string {
  return v != null && v !== 0 ? `₩${v.toLocaleString()}` : '—'
}
